import type { TicketCoverageInfo } from '../utils/coverageModel'; // FIX-D300-S2
import type {
  QuoteEvent,
  QuoteRevisionRef,
  QuoteLifecycle,
  ContractStatus,
  DocumentLink,
  DocumentSignature,
} from './sales';

// User roles
export type UserRole = 'admin' | 'agent' | 'customer_manager' | 'customer_viewer';
export type BillingModel = 'hourly' | 'retainer' | 'hour_bank' | 'fixed_price';
export type TicketStatus = 'planning' | 'in_progress' | 'in_review' | 'testing' | 'completed'
  // D-146 (P-19a) — a service request's own statuses (utils/ticketStatus.shared.ts).
  | 'new' | 'waiting_customer' | 'closed';
export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';
/**
 * Where a work log came from. `phone` (automatic call logs), `email` and
 * `integration` are written by the server; `suggested` is a work log the
 * employee confirmed from "תיעוד מוצע" (`POST /work-logs/suggestions/accept`).
 */
export type WorkLogSource = 'manual' | 'voice_ai' | 'timer' | 'phone' | 'email' | 'integration' | 'suggested' | 'ticket_reply';

export const WORK_LOG_SOURCES: readonly WorkLogSource[] = ['manual', 'voice_ai', 'timer', 'phone', 'email', 'integration', 'suggested'];

/** The Hebrew / English label of a work-log source (`workLogs.sources.*`); an unknown value is shown as is. */
export const getWorkLogSourceLabel = (source: string, t: TFunction): string =>
  (WORK_LOG_SOURCES as readonly string[]).includes(source) ? t(`workLogs.sources.${source}`) : source;

export interface Organization {
  id: string;
  name: string;
  // תיקון 1 — a Customer is NOT required to have billing terms. These are
  // nullable on the Customer record; the canonical terms live on the
  // Engagement (§49/§26). A NULL here means "no terms declared yet", never
  // "hourly at 0".
  billing_model: BillingModel | null;
  hourly_rate: number | null;
  retainer_hours?: number;
  ticket_visibility: 'own' | 'all';
  active: boolean;
  /** P-04/A6 (D-42) — the internal OTE customer (no price / terms required on its projects). */
  is_internal?: boolean;
  tax_id?: string;
  email?: string;
  phone?: string;
  phone_secondary?: string;
  joined_at?: string;
  address?: string;
  city?: string;
  website?: string;
  notes?: string;
  sla_response_hours?: number;
  sla_resolution_hours?: number;
  business_hours_start?: string;
  business_hours_end?: string;
  business_days?: number[];
  account_manager_id?: string;
  account_manager_name?: string;
  /** P-01/B2 — list rows only: open engagements by type ("סוגי עבודה"). */
  work_types?: import('./sales').CustomerWorkType[];
  /** P-07 (D-74 §5) — list rows only: an active "אפיון" card and no open engagement yet. */
  in_characterization?: boolean;
  created_at: string;
  updated_at: string;
}

export type TimerStatus = 'active' | 'suspended';
export type PreferredLanguage = 'he' | 'en';

export interface TicketFilterPreferences {
  assigned: boolean;
  commented: boolean;
  created: boolean;
  include_closed: boolean;
}

export interface User {
  id: string;
  organization_id: string;
  role: UserRole;
  name: string;
  email: string;
  phone?: string;
  active: boolean;
  timer_started_at?: string;
  timer_ticket_id?: string;
  timer_status?: TimerStatus;
  preferred_language: PreferredLanguage;
  ticket_filter_preferences: TicketFilterPreferences;
  dashboard_widget_preferences?: string[];
  /** Organizations/contacts this user may view through the customer portal (from login). */
  portal_orgs?: PortalOrg[];
  /**
   * SEC-2a (D-91 ב5) — from login: an OTE employee (employee profile). `false`
   * = a customer identity, whatever its role: it lands in /portal. Absent on
   * an older session: the role decides (`isPortalIdentity`).
   */
  is_ote_employee?: boolean;
  /** P-36b (D-196) — GET /users: an internal-only employee (never put on a customer's work). */
  internal_only?: boolean;
  created_at: string;
  updated_at: string;
}

export interface PortalOrg {
  organization_id: string;
  organization_name: string;
  portal_role: string;
  contact_id?: string | null;
  contact_name?: string | null;
  permissions?: Record<string, unknown>;
  /** Grant bag overlaid on portal_role defaults (server-computed). */
  effective_permissions?: PortalPermissions;
  /** DQ-127 (D-312 s.4): FALSE = the customer is in the archive (read-only portal). */
  organization_active?: boolean;
}

/** Granular portal permissions (decision 83) — server-computed, effective. */
export type PortalPermissionKey =
  | 'view_tickets'
  | 'create_tickets'
  | 'view_projects'
  | 'view_hours'
  | 'view_documents'
  | 'view_billing'
  | 'view_quotes'
  | 'approve_quotes'
  | 'approve_extra_hours'
  | 'approve_tickets'
  | 'view_all_tickets';
export type PortalPermissions = Record<PortalPermissionKey, boolean>;

export interface PortalPermissionsResponse {
  is_staff: boolean;
  organizations: Record<string, PortalPermissions>;
  any: Partial<PortalPermissions>;
  /** DQ-127 (D-312 s.4): the customers in the archive - their portal is read only. */
  archived_organizations?: string[];
}

// --- Customer portal (§50/§51) entity shapes that the portal endpoints return ---
export interface PortalEngagement {
  id: string;
  type: string;
  organization_id: string;
  name?: string | null;
  status?: string | null;
  child_project_id?: string | null;
  project_name?: string | null;
  project_status?: string | null;
  created_at?: string;
  /** P-10e (D-82) — the project / development's progress (null for other types). */
  progress?: number | null;
}

/** D-268 §2 — the characterization card in the portal (no price / hours / costs). */
export interface PortalCharacterizationTask {
  id: string;
  title: string;
  status: string;
  progress: number;
  due_date?: string | null;
}
export interface PortalCharacterization {
  id: string;
  organization_id: string;
  name: string;
  status: string;
  created_at?: string;
  progress?: number | null;
  /** D-293: price before VAT, only with the money permission. */
  price?: number | null;
  price_incl_vat?: number | null;
  tasks: PortalCharacterizationTask[];
  documents: PortalDocument[];
}

export interface PortalMilestone {
  id: string;
  name: string;
  status: string;
  due_date?: string | null;
  start_date?: string | null;
  estimated_hours?: number | null;
  project_id?: string;
  project_name?: string;
  /** P-10e (D-82) — the milestone's progress bar. */
  progress?: number | null;
}

export interface PortalDocument {
  id: string;
  title: string;
  document_type: string;
  status?: string;
  description?: string | null;
  /** Q-DQ-295: the latest version's file name (decides whether a preview button is shown). */
  latest_file_name?: string | null;
  has_file?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface PortalHoursRow {
  organization_id: string;
  organization_name: string;
  work_log_count: number;
  total_billable_hours: number;
  total_hours: number;
  /** DQ-121: bank hours beyond the balance (locked, not billed); subtracted for display only. */
  locked_hours?: number | string | null;
}

export interface PortalObligation {
  id: string;
  title: string;
  amount: number;
  currency?: string;
  issue_date?: string;
  due_date?: string | null;
  payment_status: string;
  paid_at?: string | null;
  obligation_type?: string;
  organization_id?: string;
  organization_name?: string;
  /** The remaining balance (amount + adjustments − payments; the server's billing_obligation_outstanding). */
  outstanding?: number | string | null;
  /** DQ-166: the sum of credits / adjustments on the charge (the shown total is amount + this). */
  adjustments_total?: number | string | null;
}

export interface PortalSummaryData {
  organization_count: number;
  engagement_count: number;
  open_engagements: number;
  pending_obligations: number;
  total_open_amount: number;
  total_debt: number;
}

export interface TicketAgent {
  id: string;
  name: string;
  role: UserRole;
  assigned_at?: string;
}

export interface TicketMilestone {
  id: string;
  name: string;
  status: string;
  due_date?: string;
  assignee_id?: string;
  assignee_name?: string;
  assignees?: { id: string; name: string }[];
}

export type TaskType = 'ticket' | 'task' | 'discussion' | 'milestone';

export interface Ticket {
  /** D-255: employees with a running timer on it now (staff view only). */
  working_now?: Array<{ name: string; since?: string }>;
  /** FIX-D300-S2 (D-300): a service request - its customer's coverage + the request to the manager (staff view only). */
  coverage?: TicketCoverageInfo | null;
  /** Portal request approved by the customer's ticket approver (approver spec §3.5). */
  customer_approved_by_name?: string | null;
  customer_approved_at?: string | null;
  id: string;
  organization_id: string;
  customer_id: string;
  /** P-43 (T-110) — GET /tickets/:id (staff): a request that arrived with no customer, so "שיוך ללקוח" is offered. */
  is_unlinked?: boolean;
  /** P-39 (D-199) — GET /tickets (staff): unread for the caller (a message, a manual "סמן כלא נקרא", or a new ticket not opened yet). */
  is_unread?: boolean;
  /** P-36 (D-193) — who opened it (GET /tickets/:id); a discussion is closed by this person or the manager. */
  opened_by_id?: string | null;
  contact_id?: string;
  title: string;
  description?: string;
  status: TicketStatus;
  priority: TicketPriority;
  assigned_agent_id?: string;
  project_id?: string;
  parent_ticket_id?: string;
  task_type: TaskType;
  /** P-45b (D-210) — a discussion opened as a conversation. */
  is_conversation?: boolean;
  estimated_hours?: number;
  actual_hours: number;
  /** P-30c (D-175, QX-40) — the part of actual_hours that is not billable. */
  non_billable_hours?: number | string | null;
  order_index: number;
  billing_model_override?: BillingModel;
  hourly_rate_override?: number;
  customer_summary?: string;
  due_date?: string;
  effective_due_date?: string;
  due_date_source?: 'ticket' | 'milestone' | 'project';
  first_response_at?: string;
  sla_response_due_at?: string;
  sla_resolution_due_at?: string;
  sla_response_breached?: boolean;
  sla_resolution_breached?: boolean;
  sla_response_warning_sent_at?: string | null;
  sla_resolution_warning_sent_at?: string | null;
  ticket_number?: number;
  progress_percent?: number;
  approval_status?: 'pending' | 'approved' | 'rejected';
  approved_by?: string;
  approved_at?: string;
  bug_report_context_id?: string;
  source?: TicketSource;
  opened_at: string;
  closed_at?: string;
  // §18 — cancellation is a SEPARATE action from the 5-value status. It lives in
  // these dedicated fields (NOT a status value). cancelled_at set ⇒ ticket cancelled.
  cancel_reason?: string;
  cancelled_at?: string;
  created_at: string;
  updated_at: string;
  /** Who created / last changed the task (`GET /tickets/:id`; older servers omit them). */
  created_by?: string | null;
  created_by_name?: string | null;
  updated_by?: string | null;
  updated_by_name?: string | null;
  customer_name?: string;
  agent_name?: string;
  organization_name?: string;
  project_name?: string;
  /** P-02b — the task's OWN engagement (`tickets.engagement_id`), its type and label. */
  engagement_id?: string | null;
  engagement_type?: EngagementType | null;
  engagement_name?: string | null;
  agents?: TicketAgent[];
  milestones?: TicketMilestone[];
  // Phase K3 — additional customers (beyond the primary customer_id) for discussions
  extra_customers?: { id: string; name: string; role: UserRole }[];
  /** GET /tickets/:id — the ticket's organization is the company itself (internal work). */
  organization_is_internal?: boolean;
  /** P-06 — the task's milestone (`tickets.milestone_id`) and its start date. */
  milestone_id?: string | null;
  start_date?: string | null;
  /** P-06/D (D-60) — "משימות קשורות" (`GET /tickets/:id`, tasks only). */
  related_tasks?: RelatedTask[];
  /** SEC-2b (D-94) — GM · AM · PM · TL of this task: may delete it (when no work was recorded). */
  can_manage_task?: boolean;
  /** P-37 (D-194 Q-179) — the ticket this task was created from (staff). */
  source_ticket_id?: string | null;
  source_ticket?: TicketLinkRef | null;
  /** P-37 — the tasks created from this ticket (staff). */
  created_tasks?: TicketLinkRef[];
}

/** P-37 — a ticket / task on the other side of "יצירת משימה מהקריאה". */
export interface TicketLinkRef {
  id: string;
  title: string;
  ticket_number: number | null;
  status: string;
}

/** P-06/D (D-60) — one row of "משימות קשורות". */
export interface RelatedTask {
  id: string;
  title: string;
  status: TicketStatus;
  priority?: TicketPriority;
  due_date?: string | null;
  milestone_id: string | null;
  milestone_name: string | null;
  milestone_parent_id?: string | null;
  /** same = the task's milestone · milestone = another milestone · direct = under the project · customer = same customer (service / bank / לל"ח). */
  group: 'same' | 'milestone' | 'direct' | 'customer';
  assignees: { id: string; name: string }[];
}

/** P-06/C (D-59) — the data of "לשייך גם לאבן הדרך ולפרויקט?". */
export interface ParentAssignmentCheck {
  milestone: { id: string; name: string } | null;
  project: { id: string; name: string } | null;
  agents: { id: string; name: string | null; in_milestone: boolean; in_project: boolean }[];
  ask: boolean;
}

export interface WorkLog {
  /** EQ-019 (D-262): a לל"ח hour's customer-approval state; null = no approval policy. */
  billing_approval_status?: 'pending' | 'approved' | 'rejected' | null;
  /** EQ-019: approved by an OTE manager in the customer's name. */
  billing_approved_by_ote?: boolean | null;
  id: string;
  /** D-266 sec.1: 'pending' = beyond the quota / bank, waits for the approval / a new bank; 'released' = approved. */
  over_quota_status?: 'pending' | 'released' | null;
  /** D-266 sec.1: the engagement type of an over-quota log (the tag: contract "מעבר למכסה" / bank "נעולות"). */
  over_quota_kind?: string | null;
  /** EQ-014: for a bank log, the locked part and the whole bank draw, in minutes (computed on the server). */
  over_quota_locked_minutes?: number | null;
  over_quota_total_minutes?: number | null;
  /** D-287: used_on of the row marked as the overrun, or null. */
  over_quota_origin_date?: string | null;
  /** P-10c (D-76): an approved service-contract month — not editable until an OTE manager re-opens it. */
  service_log_locked?: boolean;
  ticket_id: string;
  agent_id: string;
  source: WorkLogSource;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  summary: string;
  ai_confidence?: number;
  requires_review: boolean;
  reviewed: boolean;
  reviewed_by?: string;
  reviewed_at?: string;
  billable: boolean;
  project_id?: string;
  organization_id?: string;
  work_category?: WorkCategory;
  milestone_id?: string;
  participant_count?: number;
  participants?: { user_id: string; name: string }[];
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  // Stage G — each work-log row now also carries the id of its parent
  // Work Record (the grouping entity introduced by Stage G). Optional so it
  // stays compatible with older/other consumers that don't bind records.
  work_record_id?: string;
  ticket_title?: string;
  milestone_name?: string;
  milestone_status?: string;
  ticket_status?: string;
  /** The task was cancelled (GET /work-logs) — left out of "המשימות האחרונות שלי". */
  ticket_cancelled?: boolean;
  task_type?: string;
  customer_id?: string;
  customer_name?: string;
  agent_name?: string;
  assigned_agent_name?: string;
  organization_name?: string;
  /** D-319 sec.9: the task's project / engagement (names for the grouped views). */
  project_name?: string | null;
  engagement_id?: string | null;
  engagement_name?: string | null;
  /** D-319 sec.9: the employee confirmed the month of this log (from the work-logs API; missing = false). */
  month_approved?: boolean;
}

// ==========================================
// Work Records (Stage G)
// A Work Record groups one or more work-log segments (and timer sessions) of
// the same worker against the same ticket. It can be `open` (still accruing
// segments) or `closed` (finished, optionally with a summary).
// Aggregates / joined fields are optional in the type; the records list
// endpoint provides them, but the timer endpoints may only return a subset.
// ==========================================
export type WorkRecordStatus = 'open' | 'closed';

export interface WorkRecord {
  id: string;
  ticket_id: string;
  agent_id: string;
  milestone_id?: string;
  status: WorkRecordStatus;
  billable: boolean;
  work_category?: WorkCategory;
  summary?: string;
  requires_review: boolean;
  reviewed: boolean;
  reviewed_by?: string;
  reviewed_at?: string;
  created_at: string;
  updated_at: string;
  closed_at?: string;
  deleted_at?: string;
  // joined / aggregate fields (from GET /worklogs/records)
  ticket_title?: string;
  ticket_status?: string;
  task_type?: string;
  project_id?: string;
  project_name?: string;
  /** The task's own engagement (P-02b): shown when the task has no project. */
  engagement_id?: string | null;
  engagement_type?: EngagementType | null;
  engagement_name?: string | null;
  organization_id?: string;
  organization_name?: string;
  customer_id?: string;
  customer_name?: string;
  agent_name?: string;
  milestone_name?: string;
  milestone_status?: string;
  segments_count?: number;
  total_minutes?: number;
  billable_minutes?: number;
  first_started_at?: string;
  last_ended_at?: string;
  segments?: WorkLog[];
}

export interface WorkRecordListResponse {
  records: WorkRecord[];
  total: number;
  limit?: number;
  offset?: number;
}

// ==========================================
// Timer / Stage G status
// ==========================================
// B.18 — an employee may run several timers at once (one per task).
export interface ActiveTimer {
  id: string;
  user_id: string;
  ticket_id: string;
  ticket_title?: string | null;
  ticket_number?: number | null;
  work_record_id: string | null;
  started_at: string;
  suspended_at: string | null;
  status: 'running' | 'suspended';
  segments_count?: number;
  /** P-10a (D-64) — the task's status (status circles after ⏸ and in ✓). */
  ticket_status?: string | null;
  /** P-10e (D-82) — the task's saved percent (the progress bar). */
  ticket_progress?: number | null;
  task_type?: string | null;
  /** P-37 (D-194) — 'reply' = "אני על זה" on a ticket (a "מענה לקריאה" log). */
  kind?: 'work' | 'reply';
  /** P-37 (Q-182) — paused automatically by this reply timer. */
  paused_by_timer_id?: string | null;
  /** D-270: the task's seconds today before this timer started (after a break, or ⏸ then ▶). */
  accumulated_today_seconds?: number;
}

/** P-37 — a task timer paused / resumed by "אני על זה". */
export interface PausedTimerInfo {
  timer_id: string;
  ticket_id: string;
  ticket_title: string | null;
}

/** P-37 — POST /timer/reply/start. */
export interface ReplyTimerStartData {
  timer_id: string;
  ticket_id: string;
  started_at: string;
  kind: 'reply';
  paused: PausedTimerInfo[];
  debt_warning?: unknown;
}

/** P-37 — the 00:00 automatic clock-out of one day, and its morning answer. */
export interface AutoCloseout {
  id: string;
  work_date: string;
  closed_at: string;
  clock_out_at: string | null;
  suggested_at: string | null;
  status: 'pending' | 'confirmed' | 'changed';
  timers: Array<{
    work_log_id: string | null;
    ticket_id: string;
    ticket_title: string | null;
    started_at: string | null;
    suggested_end: string | null;
    end_time: string | null;
  }>;
}

// An open Work Record without a running timer (finish/document candidate).
export interface OpenWorkRecordInfo {
  work_record_id: string;
  ticket_id: string;
  ticket_title: string | null;
  ticket_status?: string | null;
  ticket_progress?: number | null;
  task_type?: string | null;
  /** QA-3 #8 — the task was cancelled. */
  ticket_cancelled?: boolean;
  created_at: string;
  segments_count: number;
}

export interface TimerStatusData {
  timers: ActiveTimer[];
  open_records: OpenWorkRecordInfo[];
  open_record: OpenWorkRecordInfo | null;
  // Legacy single-timer mirror of the primary timer (older consumers).
  timer_started_at?: string;
  timer_ticket_id?: string;
  timer_status?: 'active' | 'suspended';
  ticket_title?: string;
  work_record_id?: string;
  work_record_status?: 'open' | null;
  segments_count?: number;
}

// Result of starting the timer. When a fresh timer is started it creates the
// open work record (id/work_record_id are present once created).
export interface TimerStartData {
  id: string | null;
  timer_id?: string;
  ticket_id: string;
  ticket_title?: string;
  work_record_id?: string | null;
  timer_started_at?: string;
  /** D-110 — the customer has an overdue debt: a warning, never a block. */
  debt_warning?: DebtWarning | null;
}

/** D-110 — "⚠️ ללקוח X חוב פתוח (₪..., באיחור N ימים)". */
export interface DebtWarning {
  organization_id: string;
  organization_name: string;
  amount: number;
  days_overdue: number;
  message: string;
}

// Result of stopping the timer (persists the active segment as a work_log and
// clears the active timer, leaving the work record OPEN for later).
export interface TimerStopData {
  id: string;
  work_record_id?: string;
  ticket_id: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  cumulative_minutes: number;
  /** P-37 — 'reply' = "אני על זה"; `logged` false = under a minute, nothing written. */
  kind?: 'work' | 'reply';
  logged?: boolean;
  /** FQ-062 — a work burst under a minute: nothing was saved. */
  skipped_short?: boolean;
  resumed?: PausedTimerInfo[];
  /** FIX-B17 (QX-147) — tasks left paused by "אני על זה", offered once now. */
  resume_offer?: PausedTimerInfo[];
}

// Result of finishing a work record (stop + document + close in one action).
export interface TimerFinishData {
  /** FQ-062 (Q-176) — under a minute and nothing else on the record: nothing was saved. */
  skipped_short?: boolean;
  work_record: WorkRecord;
  segments: WorkLog[];
  total_minutes: number;
  segments_count: number;
  /** FIX-B17 (QX-147) — tasks left paused by "אני על זה", offered once now. */
  resume_offer?: PausedTimerInfo[];
}

export interface WorkLogCustomerSummary {
  customer_id: string;
  customer_name: string;
  total_minutes: number;
  billable_minutes: number;
  record_count: number;
}

export type WorkCategory = 'internal' | 'development' | 'service' | 'hour_bank' | 'consultation';

export interface VoiceEntry {
  id: string;
  organization_id: string;
  agent_id: string;
  audio_url: string;
  audio_duration_seconds?: number;
  transcription_text?: string;
  parsed_payload?: any;
  processing_status: 'pending' | 'processing' | 'processed' | 'failed';
  confidence?: number;
  work_log_id?: string;
  error_message?: string;
  created_at: string;
  processed_at?: string;
}

/**
 * ב6 — the organization a login returns: the server sends only these fields
 * (an allow-list), never the full customer row.
 */
export type AuthOrganization = Pick<Organization, 'id' | 'name'>;

export interface AuthResponse {
  token: string;
  user: User;
  organization: AuthOrganization;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    message: string;
    code: number;
  };
  meta?: {
    limit?: number;
    offset?: number;
    count?: number;
    total?: number;
    /** GET /api/leads: the three list tabs' counts (D-216 s.2). */
    tab_counts?: { active: number; converted: number; irrelevant: number };
    customer_summary?: WorkLogCustomerSummary[];
    /** P-18 (D-134) — GET /api/contacts: what the caller may do on the contacts screens. */
    viewer?: ContactsViewer;
  };
}

/** P-18 (D-134) — the caller's reach on contacts (GET /api/contacts `meta.viewer`). */
export interface ContactsViewer {
  is_general_manager: boolean;
  can_create: boolean;
  can_grant_access: boolean;
  managed_org_ids: string[];
  multi_customer: boolean;
}

/** A row of the central control audit (audit_log). */
export interface AuditTrailEntry {
  id: string;
  entity_type: string;
  entity_id: string;
  actor_id: string | null;
  actor_type: 'user' | 'api_key' | 'system';
  api_key_id: string | null;
  action: string;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  reason: string | null;
  ip_address: string | null;
  created_at: string;
  actor_name: string | null;
  actor_email: string | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface CreateWorkLogDto {
  ticket_id: string;
  start_time: string;
  end_time: string;
  summary: string;
  billable?: boolean;
  milestone_id?: string;
  /** u3 — chosen hour-bank purchase / authorized override reason. */
  hour_bank_id?: string;
  override_reason?: string;
  /** FU-53 (D-146 Q-98): the engagement for a ticket that has none yet. */
  engagement_id?: string;
  /** D-299 (QX-1302): the employee confirmed they worked in parallel - an overlap is allowed. */
  confirm_parallel?: boolean;
}

export interface CreateTicketDto {
  customer_id: string;
  title: string;
  description?: string;
  priority?: TicketPriority;
  assigned_agent_id?: string;
  task_type?: TaskType;
  contact_id?: string;
  due_date?: string;
  organization_id?: string;
  project_id?: string;
  engagement_id?: string;
  source?: string;
  // Phase K3 — multi-participant discussions
  extra_customer_ids?: string[];
  additional_agent_ids?: string[];
  /** P-45a (D-210): a discussion opened as a CONVERSATION (also with files: FIX-CONVPORTAL). */
  is_conversation?: boolean;
}

export interface DashboardSummary {
  tickets: { total: number; active: number };
  hours: { total_minutes: number; billable_minutes: number };
  organizations: number;
  /** null for non-GM staff (no meaningful user population in their scope). */
  users: number | null;
  monthly_revenue: number;
  /** P-28b (D-169) - this month's revenue / cost / profit for the OTE manager; null for anyone else. */
  monthly_business?: import('../components/profitability/profitabilityModel').MonthlyBusiness | null;
  recent_work_logs: WorkLog[];
}

export interface BillingSummary {
  organization_id: string;
  organization_name: string;
  billing_model: BillingModel;
  hourly_rate: number;
  retainer_hours?: number;
  total_logs: number;
  total_hours: number;
  billable_hours: number;
  total_amount: number;
  ticket_count: number;
  /** QX-1288: the customer's active engagements and what is charged this month. */
  engagements?: { engagement_id: string; type: string; name: string | null; amount: number; estimated: boolean }[];
  /** QX-1288: the part of total_amount not on a listed engagement. */
  other_amount?: number;
}

export interface AuditLog {
  id: string;
  work_log_id: string;
  changed_by: string;
  changed_by_name: string;
  change_type: 'created' | 'updated' | 'deleted';
  old_values: any;
  new_values: any;
  changed_at: string;
  ticket_title: string;
}

// i18n-aware label helpers — pass the `t` function from useTranslation()
export type TFunction = (key: string) => string;

/** D-146 — pass the row's task_type: a service request ('ticket') reads "בטיפול" / "הושלמה" (ticketStatus.*). */
export const getStatusLabel = (status: TicketStatus, t: TFunction, taskType?: string | null): string =>
  // D-154 (QX-85) — a discussion reads "פתוח" / "סגור".
  taskType === 'discussion'
    ? t(status === 'completed' || status === 'closed' ? 'discussionStatus.closed' : 'discussionStatus.open')
    : (taskType === 'ticket' || status === 'new' || status === 'waiting_customer' || status === 'closed')
    && ['new', 'in_progress', 'waiting_customer', 'completed', 'closed'].includes(status)
    ? t(`ticketStatus.${status}`)
    : t(`status.${status}`);

export const getPriorityLabel = (priority: TicketPriority, t: TFunction): string =>
  t(`priority.${priority}`);

export const getStatusLabels = (t: TFunction): Record<TicketStatus, string> => ({
  planning: t('status.planning'),
  in_progress: t('status.in_progress'),
  in_review: t('status.in_review'),
  testing: t('status.testing'),
  completed: t('status.completed'),
  new: t('ticketStatus.new'),
  waiting_customer: t('ticketStatus.waiting_customer'),
  closed: t('ticketStatus.closed'),
});

export const getPriorityLabels = (t: TFunction): Record<TicketPriority, string> => ({
  low: t('priority.low'),
  medium: t('priority.medium'),
  high: t('priority.high'),
  urgent: t('priority.urgent'),
});

export const getUserRoleLabel = (role: UserRole, t: TFunction): string =>
  t(`userRole.${role}`);

export const getUserRoleLabels = (t: TFunction): Record<UserRole, string> => ({
  admin: t('userRole.admin'),
  agent: t('userRole.agent'),
  customer_manager: t('userRole.customer_manager'),
  customer_viewer: t('userRole.customer_viewer'),
});

// ==========================================
// Ticket Timeline, Comments, Attachments
// ==========================================

export type TicketEventType =
  | 'customer_approved'
  | 'ticket_created'
  | 'status_changed'
  | 'priority_changed'
  | 'agent_assigned'
  | 'agent_removed'
  | 'comment_added'
  | 'comment_deleted'
  | 'attachment_uploaded'
  | 'attachment_deleted'
  | 'ticket_reopened';

export interface TicketAttachment {
  id: string;
  ticket_id: string;
  comment_id?: string;
  uploader_id: string;
  uploader_name?: string;
  original_name: string;
  stored_filename: string;
  mime_type: string;
  file_size_bytes: number;
  url: string;
  created_at: string;
}

export interface TimelineItem {
  id: string;
  item_type: 'event' | 'comment';
  created_at: string;
  // common
  actor_id?: string;
  actor_name?: string;
  actor_role?: UserRole;
  is_internal: boolean;
  is_deleted: boolean;
  // event fields
  event_type?: TicketEventType;
  metadata?: Record<string, any>;
  // comment fields
  comment_id?: string;
  content?: string | null;
  attachments?: TicketAttachment[];
  edited_at?: string;
  receipt_status?: 'sent' | 'delivered' | 'read' | null;
  reply_to_comment_id?: string | null;
  reply_to_content?: string | null;
  reply_to_author_name?: string | null;
  /** The comment's origin (`ticket_comments.kind`), e.g. 'task_finished' for "סיום משימה" (D-125 PM-01). */
  comment_kind?: string | null;
}

export interface TicketTimeline {
  timeline: TimelineItem[];
  ticket_attachments: TicketAttachment[];
}

export interface ConversationSummary {
  id: string;
  title: string;
  status: TicketStatus;
  task_type: TaskType;
  /** P-45b (D-210) — a discussion opened as a conversation. */
  is_conversation?: boolean;
  organization_id: string;
  organization_name: string;
  customer_name: string | null;
  last_comment: string | null;
  last_comment_at: string | null;
  last_comment_by: string | null;
  unread_count: number;
  comment_count: number;
}

export interface ConversationGroup {
  group_key: string;
  /** Human-readable row title (customer name for staff, agent name for customers) */
  display_name?: string | null;
  participants: Array<{
    id: string;
    name: string;
    role: UserRole;
  }>;
  customer_id: string;
  customer_name: string | null;
  organization_id: string;
  organization_name: string;
  last_activity_at: string | null;
  total_unread: number;
  tickets: Array<{
    id: string;
    title: string;
    status: TicketStatus;
    task_type: TaskType;
    is_conversation?: boolean;
    customer_id: string;
    customer_name: string | null;
    organization_id: string;
    organization_name: string;
    last_comment: string | null;
    last_comment_at: string | null;
    last_comment_by: string | null;
    unread_count: number;
  }>;
}

export interface TicketMentionResult {
  id: string;
  title: string;
  ticket_number: number;
  task_type: TaskType;
  status: TicketStatus;
  customer_id: string;
  customer_name: string | null;
}

export interface CreateCommentDto {
  content: string;
  is_internal?: boolean;
  reply_to_comment_id?: string;
  /** D-146 — "שליחת עדכון ללקוח" with "מחכה לתשובה": the ticket moves to "ממתינה ללקוח". */
  await_reply?: boolean;
  /** P-37 (D-194 Q-174) — the composer's "טיימר מענה": seconds of the reply (the server caps it). */
  reply_seconds?: number;
  /** P-37 (Q-178) — the engagement for the reply time, when the server asked. */
  engagement_id?: string;
  /** FIX-D251 §1 — "שאלה ללקוח" from a customer task: also sent to the customer as a portal conversation. */
  to_customer?: boolean;
}

// ==========================================
// Project & Milestone Types
// ==========================================

// P-02b — a projects row is a project or a development (service / hour-bank shadow rows are retired).
export type ProjectType = 'project' | 'development';
export type ProjectStatus = 'planning' | 'active' | 'in_progress' | 'on_hold' | 'testing' | 'completed' | 'cancelled';
export type ProjectBillingModel = 'hourly' | 'hour_bank' | 'fixed_price';
export type MilestoneStatus = 'planning' | 'in_progress' | 'in_review' | 'testing' | 'completed';
export type RenewalType = 'one_time' | 'recurring';

export interface Project {
  id: string;
  organization_id: string;
  name: string;
  type: ProjectType;
  description?: string;
  customer_summary?: string;
  status: ProjectStatus;
  /** D-303 — "אחריות ללקוח": the date, or "אין אחריות". */
  warranty_until?: string | null;
  no_warranty?: boolean;
  billing_model?: ProjectBillingModel;
  hourly_rate?: number;
  fixed_price?: number;
  total_budget_hours?: number;
  renewal_type?: RenewalType;
  renewal_day?: number;
  show_hours_to_customer: boolean;
  start_date?: string;
  end_date?: string;
  deadline_warning_days?: number;
  sla_response_hours?: number | null;
  sla_resolution_hours?: number | null;
  actual_hours?: number;
  created_by?: string;
  created_at: string;
  updated_at: string;
  /** P-02a — set by "ביטול הפרויקט" (a reason is required). */
  cancellation_reason?: string | null;
  cancelled_at?: string | null;
  /** P-02a — a cancelled project moved to the archive. */
  archived_at?: string | null;
  /** P-02a — GET /projects/:id (staff): the project is empty, so it may be deleted. */
  deletable?: boolean;
  // ---- P-04/A (D-41, D-39, D-31) ----
  project_manager_id?: string | null;
  team_lead_id?: string | null;
  project_manager_name?: string | null;
  team_lead_name?: string | null;
  current_phase_manual?: CorePhase | null;
  planned_hours_mode?: PlannedHoursMode;
  planned_hours_manual?: number | string | null;
  server_monthly_fee?: number | string | null;
  server_monthly_cost?: number | string | null;
  payment_terms?: string | null;
  engagement_id?: string | null;
  /** 'project' | 'development' (the engagement's type). */
  kind?: ProjectType;
  organization_is_internal?: boolean;
  /** P-04 — progress / phase / planned + actual hours / health (GET /projects, /projects/:id). */
  metrics?: ProjectMetrics | null;
}

/** P-04/A2 — the 4 core phases. */
export type CorePhase = 'development' | 'qa' | 'delivery' | 'fixes';
/** P-04/A3. */
export type PlannedHoursMode = 'undefined' | 'automatic' | 'manual';
/** P-04/A5 (D-40). */
export type ProjectHealthState = 'late' | 'over_hours' | 'attention' | 'ok';

/** P-04 — the figures of a project (projectMetrics.service). */
export interface ProjectMetrics {
  project_id: string;
  kind: string;
  progress: number;
  complete: boolean;
  current_phase: CorePhase | null;
  current_phase_auto: CorePhase | null;
  current_phase_manual: CorePhase | null;
  /** The phase shown next to the status ("פעיל — QA"), or null. */
  display_phase: CorePhase | null;
  planned_hours_mode: PlannedHoursMode;
  /** null = "לא מוגדר". */
  planned_hours: number | null;
  /** DQ-179: the viewer reads only through own tasks - the size is hidden (show "—", not "לא מוגדר"). */
  size_masked?: boolean;
  estimated_hours_sum: number;
  actual_hours: number;
  health: ProjectHealthState;
  overdue_days: number | null;
}

/** P-04/C — the financial block (only for who may see it). */
export interface ProjectFinancials {
  fixed_price: number | string | null;
  server_monthly_fee: number | string | null;
  server_monthly_cost: number | string | null;
  payment_terms: string | null;
  payment_schedule: Array<{ index: number; of: number; percent: number; amount: number | null }>;
}

/** P-04/C — the "שעות" tab. */
export interface ProjectHours {
  planned_hours_mode: PlannedHoursMode;
  planned_hours: number | null;
  actual_hours: number;
  difference: number | null;
  by_employee: Array<{ user_id: string; name: string | null; hours: number; percent: number }>;
  by_milestone: Array<{ milestone_id: string; name: string; is_core: boolean; archived: boolean; planned_hours: number; actual_hours: number }>;
  no_milestone: { planned_hours: number; actual_hours: number };
}

/** P-04/C — one person of the "צוות" tab. */
export interface ProjectTeamPerson {
  user_id: string;
  name: string | null;
  email: string | null;
  roles: Array<'project_manager' | 'team_lead' | 'member' | 'milestone_assignee' | 'task_assignee'>;
  member: boolean;
  hours: number;
}

export interface ProjectStats {
  total_estimated_hours: number;
  total_actual_hours: number;
  budget_hours_remaining: number;
  tickets_total: number;
  tickets_closed: number;
  completion_percentage: number;
  billing_config: BillingConfig;
}

export interface BillingConfig {
  model: ProjectBillingModel;
  rate?: number;
  fixedPrice?: number;
  budgetHours?: number;
  renewalType?: RenewalType;
}

// Stage D2 — project-scoped assignments (PMs + team members).
// These are project-scoped assignments (project_managers / project_members),
// NOT global auth roles. Team Lead is `member`-record with role 'team_lead'.
export interface ProjectManagerAssignment {
  assignment_id: string;
  user_id: string;
  name: string;
  email?: string;
  role: string; // global auth role (admin/agent/...)
  assigned_by?: string;
  assigned_at: string;
}

export interface ProjectTeamMember {
  assignment_id: string;
  user_id: string;
  name: string;
  email?: string;
  role: string; // 'team_lead' | 'member' (project-scoped record role)
  auth_role?: string; // global auth role for display
  assigned_by?: string;
  assigned_at: string;
}

export interface ProjectAssignments {
  managers: ProjectManagerAssignment[];
  members: ProjectTeamMember[];
}

export interface Milestone {
  id: string;
  // Runtime XOR parent: exactly one of a project or a development
  // engagement-child. project_id is null for development-bound milestones.
  project_id: string | null;
  development_id?: string | null;
  // Canonical: the milestone's first child task (tasks → milestone via tickets.milestone_id).
  // Returned by the milestone list endpoint (GET /api/milestones/all) as the
  // task a milestone-anchored timer should tick on. Never read legacy ticket_id for this.
  primary_task_id?: string;
  // LEGACY — legacy `milestones.ticket_id` (milestone-as-child-of-ticket). The canonical
  // direction is tasks → milestone via `tickets.milestone_id`. Do NOT use ticket_id as a
  // read link in new code; it is scheduled for backend removal and may be undefined.
  ticket_id?: string;
  name: string;
  description?: string;
  start_date?: string;
  due_date?: string;
  status: MilestoneStatus;
  order_index: number;
  estimated_hours?: number;
  actual_hours?: number;
  created_at: string;
  /** P-02a — one of the 4 core milestones (fixed name; never deleted or archived). */
  is_core?: boolean;
  core_phase?: 'development' | 'qa' | 'delivery' | 'fixes' | null;
  /** P-02a — archived (a milestone with work is archived instead of deleted). */
  archived_at?: string | null;
  /** P-02a — GET /projects/:id: a ticket or a work log points to it (not empty). */
  has_work?: boolean;
  /** P-05/A1 — the responsible ("אחראי", optional). */
  responsible_user_id?: string | null;
  responsible_name?: string | null;
  /** P-05/A3 — estimated hours: undefined ("לא מוגדר") / automatic / manual. */
  planned_hours_mode?: 'undefined' | 'automatic' | 'manual';
  planned_hours_manual?: number | string | null;
  /** P-05/A2 — the parent of a sub-milestone (one level). */
  parent_milestone_id?: string | null;
  parent_milestone_name?: string | null;
  /** P-05/E — GET /projects/:id: the assignees ("מצוותים"), sub-milestones exist, the figures. */
  assignees?: { user_id: string; name: string }[];
  has_sub_milestones?: boolean;
  metrics?: MilestoneMetrics | null;
}

/** P-05 — a milestone's figures (projectMetrics.loadMilestoneMetrics). */
export interface MilestoneMetrics {
  /** DQ-179: the viewer reads only through own tasks - the size is hidden. */
  size_masked?: boolean;
  milestone_id: string;
  progress: number;
  complete: boolean;
  planned_hours_mode: 'undefined' | 'automatic' | 'manual';
  /** null = "לא מוגדר". */
  planned_hours: number | null;
  estimated_hours_sum: number;
  actual_hours: number;
  task_count: number;
  sub_count: number;
  overdue_days: number | null;
}

/** P-05/G (D-53) — what the caller may do with a milestone. */
export interface MilestonePermissions {
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_change_status: boolean;
}

export interface MilestoneAssignee {
  milestone_id: string;
  user_id: string;
  assigned_at: string;
  assigned_by?: string;
}

export interface MyMilestoneAssignee {
  user_id: string;
  name: string;
  assigned_at: string;
}

export interface SiblingMilestone {
  id: string;
  name: string;
  due_date?: string;
  status: MilestoneStatus;
  order_index: number;
  tickets_total: number;
  tickets_closed: number;
  assignee_names?: string;
}

export const getProjectTypeLabel = (type: ProjectType, t: TFunction): string =>
  t(`projectType.${type}`);

export const getProjectTypeLabels = (t: TFunction): Record<ProjectType, string> => ({
  project: t('projectType.project'),
  development: t('projectType.development'),
});

export const getProjectStatusLabel = (status: ProjectStatus, t: TFunction): string =>
  t(`projectStatus.${status}`);

export const getProjectStatusLabels = (t: TFunction): Record<ProjectStatus, string> => ({
  planning: t('projectStatus.planning'),
  active: t('projectStatus.active'),
  in_progress: t('projectStatus.in_progress'),
  on_hold: t('projectStatus.on_hold'),
  testing: t('projectStatus.testing'),
  completed: t('projectStatus.completed'),
  cancelled: t('projectStatus.cancelled'),
});

export type DeadlineStatus = 'ok' | 'approaching' | 'overdue';

export const getMilestoneStatusLabel = (status: MilestoneStatus, t: TFunction): string =>
  t(`milestoneStatus.${status}`);

export const getMilestoneStatusLabels = (t: TFunction): Record<MilestoneStatus, string> => ({
  planning: t('milestoneStatus.planning'),
  in_progress: t('milestoneStatus.in_progress'),
  in_review: t('milestoneStatus.in_review'),
  testing: t('milestoneStatus.testing'),
  completed: t('milestoneStatus.completed'),
});

// ==========================================
// Notification Types
// ==========================================

export interface Notification {
  id: string;
  user_id: string;
  ticket_id?: string;
  entity_type: string;
  entity_id?: string;
  event_type: string;
  read_at?: string;
  created_at: string;
  ticket_title?: string;
  organization_name?: string;
  metadata?: {
    sender_name?: string;
    message_preview?: string;
    /** DQ-381: meeting notices carry the meeting title and time. */
    title?: string;
    meeting_datetime?: string;
    /** P-06/C (D-59) — task_assigned: the project / engagement of the task. */
    container_name?: string | null;
    container_kind?: 'project' | 'engagement';
    /** P-46: lead_assigned / lead_follow_up_due (and lead_created). */
    company_name?: string;
    /** Website leads (10-29): lead_created - the lead's source in Hebrew ("אתר"). */
    source_label?: string | null;
    /** D-265: engagement_archived / engagement_purged. */
    engagement_name?: string | null;
    /** D-329: engagement_financials_requested - the employee who asked. */
    requested_by_name?: string | null;
    /** P-10a (D-65) — task_completed_by_other: who marked the task completed. */
    actor_name?: string;
    /** SEC-2b (D-106) — task_self_created: the team members added to the task. */
    added_names?: string[];
    ticket_title?: string;
    /** P-10c (D-70) — the monthly service-log notices: 'YYYY-MM'. */
    month?: string;
    /** BQ-060/QX-1284 — timer_billed_month_hours: the kept minutes and the month they will be charged. */
    minutes?: number;
    charge_month?: string;
    /** FU-30 — portal_user_added (D-101): the new user and who added them. */
    user_name?: string;
    added_by_name?: string;
    /** FU-30 — attendance_accountant_missing: the month ('YYYY-MM-01') and the employee. */
    month_year?: string;
    /** D-273 */
    period_start?: string;
    period_end?: string;
    employee_name?: string;
    /** D-110 — customer_debt_work: the customer, its overdue amount and days late. */
    organization_name?: string;
    amount?: number;
    days_overdue?: number;
    /** P-10c (QA-10c #12) — a re-opened breakdown: the manager's reason. */
    reopened?: boolean;
    reason?: string;
    /** D-99 — task_completed_by_other / task_cancelled_by_other: a task or a service call. */
    task_type?: 'task' | 'ticket';
    /** D-303 — warranty_ending: the customer, the project and the warranty date. */
    warranty_until?: string;
    /** FIX-D300-S2 — coverage_request_*: the request (ticket) and the manager's decision. */
    ticket_id?: string;
    resolution?: 'approved' | 'hour_bank' | 'service_contract' | 'service_without_contract';
    organization_id?: string;
    /** P-30b (D-171 / Q-144) — project_hours_threshold: the project and 80 | 100. */
    project_name?: string;
    threshold_pct?: number;
    /** P-37 (D-194 Q-184) — "רץ כבר 8 שעות": the timer and its hours. */
    timer_id?: string;
    hours?: number;
    /** W-01 — custom_reminder: the text, who set it (is_self = the person themselves), the firing day and the series. */
    message?: string;
    creator_id?: string;
    creator_name?: string | null;
    is_self?: boolean;
    occurrence_id?: string;
    reminder_id?: string;
    keep_until_done?: boolean;
    /** D-341: 'bug' | 'suggestion' on bug_report_created. */
    report_kind?: string;
  };
  /** Frontend route resolved server-side (u5 link resolver). */
  link?: string;
  /** Deduped repeats batched into this notification. */
  occurrence_count?: number;
  last_occurred_at?: string;
}

// ==========================================
// Search Types
// ==========================================

export type SearchResultType =
  | 'ticket'
  | 'project'
  | 'comment'
  | 'worklog'
  | 'document'
  | 'customer'
  | 'contact'
  | 'lead'
  | 'quote'
  | 'engagement'
  | 'milestone';

export interface SearchResult {
  type: SearchResultType;
  id: string;
  title: string;
  highlight: string;
  breadcrumb: string;
  updated_at: string;
  /** Route of the entity for this caller (server-resolved). */
  url?: string;
}

export interface SearchResponse {
  results: SearchResult[];
  totals: Record<SearchResultType, number>;
}

// ==========================================
// Report Types
// ==========================================

export interface BurndownDataPoint {
  date: string;
  ideal: number;
  actual: number;
}

export interface BudgetVsActualData {
  budget: number;
  actual: number;
  percentage: number;
  currency_amount: number;
  budget_amount: number;
}

export interface MonthlyRevenuePoint {
  month: string;
  revenue: number;
}

// --- §58 Profitability (Stage טו) ---
export interface ProfitRow {
  id: string;
  name: string;
  /** P-07 — the separate "אפיון" line (D-74 §2); P-16 — 'unallocated': revenue no task / employee owns. */
  kind?: 'characterization' | 'unallocated';
  hours: number;
  revenue: number;
  cost: number;
  profit: number;
  margin: number | null;
  /** P-16 (D-127) — profit ÷ hours worked. */
  profit_per_hour?: number | null;
  /** P-16 — project level: planned hours (null = no plan) and actual − planned. */
  planned_hours?: number | null;
  deviation_hours?: number | null;
  /** D-125 (PM-14..PM-16) — the revenue kinds behind the row (installment / server / hour_bank_usage / …). */
  revenue_kinds?: string[];
}

export interface ProfitSummary {
  hours: number;
  revenue: number;
  cost: number;
  profit: number;
  margin: number | null;
}

export interface ProfitabilityData {
  level: string;
  rows: ProfitRow[];
  summary: ProfitSummary;
  /** P-13 (D-109) — active employees with no cost today: the profit is not exact. */
  cost_warning?: { employees_missing_cost: number };
}

export interface HoursByAgentData {
  agent_id: string;
  agent_name: string;
  total_hours: number;
  billable_hours: number;
}

export interface TicketsByPriorityData {
  priority: string;
  count: number;
}

export interface HoursByTicketData {
  ticket_id: string;
  ticket_title: string;
  total_hours: number;
  agent_name: string;
}

export interface AgentSummaryData {
  total_hours: number;
  billable_hours: number;
  tickets_handled: number;
  tickets_closed: number;
  avg_resolution_hours: number;
}

// --- §56 Report catalog (Stage יד) ---
export interface ReportCatalogColumn {
  label: string;
  numeric: boolean;
  key?: string;
  kind?: 'text' | 'number' | 'hours' | 'money' | 'pct' | 'date' | 'datetime' | 'bool';
}

export interface ReportCatalogItem {
  key: string;
  title: string;
  description: string;
  category: string;
  columns: ReportCatalogColumn[];
  /** Filters the report honours (u5). */
  filters?: ('date' | 'org' | 'project' | 'engagement' | 'status')[];
  default_range?: 'month' | 'six_months' | 'none';
  drilldown?: string | null;
}

export interface ReportCatalogCategory {
  key: string;
  label: string;
}

export interface ReportCatalogData {
  categories: ReportCatalogCategory[];
  reports: ReportCatalogItem[];
}

// ==========================================
// Integration Engine Types (Sprint 2)
// ==========================================

export type ProviderType = 'telephony' | 'email' | 'gmail_addon' | 'custom';
export type SourceType = 'webhook_inbound' | 'email_imap' | 'http_poll' | 'manual_trigger';
export type InboundLogStatus = 'received' | 'processing' | 'processed' | 'blocked' | 'failed';

export type PipelineStepType =
  | 'download_file'
  | 'transcribe_audio'
  | 'ai_extract'
  | 'identify_customer'
  | 'identify_agent'
  | 'check_blocklist'
  | 'apply_template'
  | 'custom_js';

export type TargetType = 'create_ticket' | 'append_comment' | 'create_work_log' | 'send_email';
export type BlocklistMatchType = 'exact' | 'normalized_phone' | 'domain';
export type BlocklistScope = 'global' | 'personal';

export interface IntegrationPreset {
  preset_key: string;
  provider_type: ProviderType;
  source_type: SourceType;
  mappings_count: number;
  steps_count: number;
  targets_count: number;
  steps: PipelineStepType[];
  targets: TargetType[];
}

export type IntegrationApprovalStatus = 'draft' | 'pending_approval' | 'approved' | 'rejected';

export interface IntegrationInstance {
  id: string;
  key: string;
  name: string;
  provider_type: ProviderType;
  preset_key: string | null;
  source_type: SourceType;
  source_config: Record<string, any>;
  enabled: boolean;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  approval_status: IntegrationApprovalStatus;
  approved_by: string | null;
  approved_at: string | null;
  rejection_reason: string | null;
  submitted_at: string | null;
}

export interface IntegrationFieldMapping {
  id: string;
  instance_id: string;
  source_field_key: string;
  target_field_key: string;
  jsonpath_expression: string;
  is_required: boolean;
  default_value: string | null;
  created_at: string;
}

export interface IntegrationPipelineStep {
  id: string;
  instance_id: string;
  step_order: number;
  step_type: PipelineStepType;
  config_json: Record<string, any>;
  enabled: boolean;
  created_at: string;
}

export interface IntegrationTarget {
  id: string;
  instance_id: string;
  target_order: number;
  target_type: TargetType;
  field_bindings: Record<string, any>;
  enabled: boolean;
  created_at: string;
}

export interface IntegrationSecretKey {
  key_name: string;
  created_at: string;
  updated_at: string;
}

export interface StepTrace {
  step_order: number;
  step_type: PipelineStepType;
  status: 'success' | 'skipped' | 'failed';
  input: Record<string, any>;
  output: Record<string, any> | null;
  error: string | null;
  duration_ms: number;
  started_at: string;
}

export interface IntegrationInboundLog {
  id: string;
  instance_id: string | null;
  source_type: string;
  status: InboundLogStatus;
  external_message_id: string | null;
  raw_payload: Record<string, any> | null;
  mapped_payload: Record<string, any> | null;
  pipeline_context: Record<string, any> | null;
  step_traces: StepTrace[];
  result_summary: Record<string, any> | null;
  error_message: string | null;
  duration_ms: number | null;
  received_at: string;
}

export interface BlocklistRule {
  id: string;
  field_name: string;
  match_value: string;
  match_type: BlocklistMatchType;
  scope: BlocklistScope;
  user_id: string | null;
  reason: string | null;
  created_by: string | null;
  created_by_name?: string;
  created_at: string;
}

export interface EmailTemplate {
  id: string;
  template_key: string;
  organization_id: string | null;
  organization_name?: string;
  locale: string;
  name?: string | null;
  description?: string | null;
  category?: string | null;
  merge_schema_key?: string | null;
  subject_template: string;
  body_template: string;
  is_system: boolean;
  is_active: boolean;
  updated_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface EmailTemplateMergeField {
  path: string;
  label: string;
  label_en?: string;
  description?: string;
  sample: string;
}

export interface EmailTemplateMergeSchema {
  key: string;
  name: string;
  fields: EmailTemplateMergeField[];
}

export interface EmailTemplatePreview {
  template_id: string;
  template_key: string;
  subject: string;
  html: string;
  text: string;
  locale: string;
}

export interface EmailTemplateAsset {
  id: string;
  url: string;
  path: string;
  mime_type: string;
  size_bytes: number;
  sha256: string;
}

export interface TestPipelineResponse {
  status: 'success' | 'failed';
  mapped_payload: Record<string, any>;
  step_traces: StepTrace[];
  context: Record<string, any>;
  error?: string;
  duration_ms: number;
}

export const getProviderTypeLabel = (type: ProviderType, t: TFunction): string =>
  t(`providerType.${type}`);

export const getProviderTypeLabels = (t: TFunction): Record<ProviderType, string> => ({
  telephony: t('providerType.telephony'),
  email: t('providerType.email'),
  gmail_addon: t('providerType.gmail_addon'),
  custom: t('providerType.custom'),
});

export const getSourceTypeLabel = (type: SourceType, t: TFunction): string =>
  t(`sourceType.${type}`);

export const getSourceTypeLabels = (t: TFunction): Record<SourceType, string> => ({
  webhook_inbound: t('sourceType.webhook_inbound'),
  email_imap: t('sourceType.email_imap'),
  http_poll: t('sourceType.http_poll'),
  manual_trigger: t('sourceType.manual_trigger'),
});

export const getStepTypeLabel = (type: PipelineStepType, t: TFunction): string =>
  t(`stepType.${type}`);

export const getStepTypeLabels = (t: TFunction): Record<PipelineStepType, string> => ({
  download_file: t('stepType.download_file'),
  transcribe_audio: t('stepType.transcribe_audio'),
  ai_extract: t('stepType.ai_extract'),
  identify_customer: t('stepType.identify_customer'),
  identify_agent: t('stepType.identify_agent'),
  check_blocklist: t('stepType.check_blocklist'),
  apply_template: t('stepType.apply_template'),
  custom_js: t('stepType.custom_js'),
});

export const getTargetTypeLabel = (type: TargetType, t: TFunction): string =>
  t(`targetType.${type}`);

export const getTargetTypeLabels = (t: TFunction): Record<TargetType, string> => ({
  create_ticket: t('targetType.create_ticket'),
  append_comment: t('targetType.append_comment'),
  create_work_log: t('targetType.create_work_log'),
  send_email: t('targetType.send_email'),
});

export const getBlocklistMatchTypeLabel = (type: BlocklistMatchType, t: TFunction): string =>
  t(`blocklistMatchType.${type}`);

export const getBlocklistMatchTypeLabels = (t: TFunction): Record<BlocklistMatchType, string> => ({
  exact: t('blocklistMatchType.exact'),
  normalized_phone: t('blocklistMatchType.normalized_phone'),
  domain: t('blocklistMatchType.domain'),
});

export const getInboundLogStatusLabel = (status: InboundLogStatus, t: TFunction): string =>
  t(`inboundLogStatus.${status}`);

export const getInboundLogStatusLabels = (t: TFunction): Record<InboundLogStatus, string> => ({
  received: t('inboundLogStatus.received'),
  processing: t('inboundLogStatus.processing'),
  processed: t('inboundLogStatus.processed'),
  blocked: t('inboundLogStatus.blocked'),
  failed: t('inboundLogStatus.failed'),
});

// ==========================================
// API Key Types
// ==========================================

export type ApiKeyScopeType = 'global' | 'org' | 'internal';

export interface ApiKeyInfo {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  scope_type: ApiKeyScopeType;
  scoped_org_id?: string;
  organization_id?: string;
  organization_name?: string;
  scoped_org_name?: string;
  last_used_at?: string;
  created_at: string;
  revoked_at?: string;
  created_by_name?: string;
  full_key?: string;
}

// ==========================================
// Webhook Types
// ==========================================

export interface WebhookEndpoint {
  id: string;
  organization_id?: string;
  created_by: string;
  url: string;
  events: string[];
  active: boolean;
  created_at: string;
  created_by_name?: string;
  organization_name?: string;
}

export interface WebhookDelivery {
  id: string;
  endpoint_id: string;
  event_type: string;
  payload?: Record<string, any>;
  response_status?: number;
  response_body?: string;
  delivered_at?: string;
  next_retry_at?: string;
  attempt_count: number;
  created_at: string;
}

// ==========================================
// System Settings Types
// ==========================================

export interface SystemSetting {
  id: string;
  category: string;
  key: string;
  value: string | null;
  encrypted: boolean;
  updated_at: string;
  updated_by?: string;
}

export interface DeployRecord {
  id: string;
  triggered_by: string;
  triggered_by_name?: string;
  status: 'running' | 'success' | 'failed' | 'rolled_back';
  started_at: string;
  completed_at?: string;
  log?: string;
  version_before?: string;
  version_after?: string;
}

export type SettingsCategory = 'general' | 'smtp' | 'ai' | 'transcription' | 'security' | 'notifications' | 'deployment' | 'attendance';

// ==========================================
// Attendance Types
// ==========================================

export type AttendanceEventType = 'clock_in' | 'clock_out' | 'break_start' | 'break_end';
export type AttendanceSource = 'web' | 'mobile' | 'kiosk' | 'admin_fix' | 'auto_midnight' | 'extension';
export type AttendanceSummaryStatus = 'auto' | 'confirmed' | 'disputed' | 'adjusted';

export interface AttendanceRecord {
  id: string;
  user_id: string;
  event_type: AttendanceEventType;
  recorded_at: string;
  source: AttendanceSource;
  notes?: string;
  adjustment_reason?: string;
  adjusted_by?: string;
  adjusted_by_name?: string;
  location_type?: string;
  created_at: string;
}

export interface AttendanceDailySummary {
  id: string;
  user_id: string;
  user_name?: string;
  work_date: string;
  total_work_minutes: number;
  total_break_minutes: number;
  regular_minutes: number;
  overtime_125_minutes: number;
  overtime_150_minutes: number;
  status: AttendanceSummaryStatus;
  employee_confirmed_at?: string;
  manager_confirmed_at?: string;
  manager_id?: string;
  notes?: string;
  events?: AttendanceRecord[];
}

export interface AttendancePolicy {
  id: string;
  organization_id?: string;
  name: string;
  business_hours_start: string;
  business_hours_end: string;
  business_days: number[];
  max_daily_hours: number;
  overtime_daily_threshold_minutes: number;
  overtime_weekly_threshold_minutes: number;
  auto_break_deduction_minutes: number;
  round_to_minutes: number;
  is_default: boolean;
  created_at: string;
}

export interface AttendanceStatus {
  status: 'clocked_in' | 'on_break' | 'clocked_out';
  since?: string;
  today_minutes: number;
  today_break_minutes: number;
  /**
   * Minutes of today's work logs of the user (every source, not deleted).
   * Sent by servers that have "תיעוד מוצע"; missing on older ones.
   */
  documented_minutes_today?: number;
  /** Running timers counted inside documented_minutes_today (the client keeps adding their time). */
  documented_live_timers?: number;
  /** D-116 — clocked out today: "חזרה לעבודה" (the clock-out becomes a break). */
  can_return_to_work?: boolean;
  /** QX-1283 — no event today and the last one is the automatic 00:00 clock-out. */
  after_auto_clockout?: boolean;
}

// ==========================================
// "תיעוד מוצע" (documentation brief, parts 1–2, 2026-10-12)
// GET /work-logs/suggestions, POST /work-logs/suggestions/accept|dismiss
// ==========================================

export type WorkLogSuggestionKind = 'ticket_activity' | 'meeting' | 'suspended_timer' | 'open_record';

export type WorkLogSuggestionActivityType =
  | 'comment' | 'status_changed' | 'attachment' | 'assigned' | 'meeting' | 'timer' | 'record';

export interface WorkLogSuggestionActivity {
  type: WorkLogSuggestionActivityType | string;
  /** ISO time of the action. */
  at: string;
  /** What happened, in the server's words (e.g. "שינוי סטטוס לבסקירה"). */
  label: string;
}

export interface WorkLogSuggestion {
  /** Stable id of the suggestion, e.g. "ticket:<ticket_id>:2026-10-12". */
  key: string;
  kind: WorkLogSuggestionKind;
  ticket_id: string | null;
  ticket_title: string | null;
  organization_id: string | null;
  organization_name: string | null;
  project_name: string | null;
  /** P-02b: the task's own engagement — shown when the task has no project. */
  engagement_id?: string | null;
  engagement_type?: EngagementType | null;
  engagement_name?: string | null;
  start_time: string;
  end_time: string;
  suggested_minutes: number;
  /** The pre-filled summary the employee can edit. */
  summary: string;
  activity: WorkLogSuggestionActivity[];
  meeting_id: string | null;
  timer_id: string | null;
  work_record_id: string | null;
}

export interface WorkLogSuggestionsData {
  /** YYYY-MM-DD (Israel time). */
  date: string;
  attendance_minutes: number;
  documented_minutes: number;
  suggestions: WorkLogSuggestion[];
}

/** D-262: a לל"ח hour that waits for the customer's approval. */
export interface PendingLllHour {
  id: string;
  start_time: string;
  duration_minutes: number;
  summary: string | null;
  agent_name: string | null;
  ticket_id: string;
  ticket_title: string | null;
  engagement_id: string;
  engagement_name: string | null;
  organization_id: string;
  month: string;
}

export interface AcceptWorkLogSuggestionDto {
  key: string;
  /** 1–600; ignored by the server for `open_record`. */
  minutes: number;
  /** 5–5000 characters. */
  summary: string;
  /** Required for a `meeting` without a ticket. */
  ticket_id?: string;
  /** BQ-052: the engagement for the work, sent only after the server asked (409 ENGAGEMENT_REQUIRED). */
  engagement_id?: string;
  /** D-299 (QX-1302): the employee confirmed they worked in parallel - an overlap is allowed. */
  confirm_parallel?: boolean;
}

export interface AttendanceVsTicketsRow {
  user_id: string;
  user_name: string;
  attendance_hours: number;
  ticket_hours: number;
  gap_percent: number;
}

/** GET /reports/team-documentation (documentation part 3, General Manager only). */
export type TeamDocPeriod = 'today' | 'week' | 'month';

export interface TeamDocEmployee {
  user_id: string;
  name: string;
  attendance_minutes: number;
  documented_minutes: number;
  /** documented / attendance, whole percent; null without attendance. */
  percent: number | null;
  pending_suggestions: number;
  stale_tasks: number;
  open_days: number;
}

export interface TeamDocumentation {
  period: TeamDocPeriod;
  from: string;
  to: string;
  /** Over the employees that have attendance. */
  totals: { attendance_minutes: number; documented_minutes: number; percent: number | null };
  /** Lowest percent first; no attendance last. */
  employees: TeamDocEmployee[];
}

export type MeetingStatus = 'scheduled' | 'completed' | 'cancelled' | 'rescheduled';
export type RsvpStatus = 'pending' | 'accepted' | 'declined' | 'tentative';

export interface Meeting {
  id: string;
  organization_id?: string;
  title: string;
  description?: string;
  meeting_datetime: string;
  duration_minutes: number;
  meeting_url?: string;
  status: MeetingStatus;
  follow_up_date?: string;
  follow_up_notes?: string;
  organizer_id: string;
  organizer_name?: string;
  organization_name?: string;
  ticket_id?: string;
  project_id?: string;
  milestone_id?: string;
  participants?: MeetingParticipant[];
  created_at: string;
  updated_at: string;
}

export interface MeetingParticipant {
  id: string;
  meeting_id: string;
  contact_id?: string;
  user_id?: string;
  rsvp_status: RsvpStatus;
  user_name?: string;
  contact_name?: string;
}

export interface ChecklistItem {
  id: string;
  ticket_id: string;
  sort_order: number;
  text: string;
  completed: boolean;
  completed_by?: string;
  completed_at?: string;
  created_by?: string;
  created_by_name?: string;
  completed_by_name?: string;
  created_at: string;
}

/** D-305 — the month's travel figures shown on the attendance page. The per-day amount / cap are card fields and never here. */
export interface MonthTravel {
  enabled: boolean;
  office_days: number;
  /** FIX-PERMS-SCOPE: absent when another employee's month is seen through "כל העובדים" (no money). */
  amount?: number;
  capped: boolean;
  locked: boolean;
}

export interface AttendanceMonthlySummary {
  id: string;
  user_id: string;
  month_year: string;
  total_work_hours: number;
  total_break_hours: number;
  total_work_days: number;
  total_travel_days: number;
  regular_hours: number;
  overtime_125_hours: number;
  overtime_150_hours: number;
  employee_confirmed: boolean;
  employee_confirmed_at?: string;
  manager_confirmed: boolean;
  manager_confirmed_at?: string;
  manager_id?: string;
  // Stage יז — attendance monthly: employee approval → accountant email → lock
  locked: boolean;
  locked_at?: string;
  accountant_notified: boolean;
  accountant_notified_at?: string;
  logs_sent_for_review: boolean;
  logs_sent_at?: string;
  employee_confirmed_logs: boolean;
  employee_confirmed_logs_at?: string;
  notes?: string;
  /** D-305 — office days are `total_travel_days`; the amount is fixed when the month is approved. */
  travel_amount?: string | number;
  travel_capped?: boolean;
  /** The employee has a travel allowance on the card (the per-day amount itself is for the OTE manager only). */
  travel_enabled?: boolean;
  created_at: string;
  updated_at: string;
}

// ==========================================
// Dashboard Builder Types
// ==========================================

export type WidgetType =
  | 'attendance_clock'
  | 'summary_cards'
  | 'billing_summary'
  | 'monthly_trend'
  | 'recent_activity'
  | 'monthly_revenue'
  | 'hours_by_agent'
  | 'tickets_by_priority'
  | 'hours_by_ticket'
  | 'agent_summary'
  | 'sla_compliance'
  | 'attendance_vs_tickets'
  | 'daily_status'
  | 'managed_clients'
  | 'project_health'
  | 'morning_brief'
  | 'client_energy'
  | 'bug_reports'
  | 'bug_report_trends'
  | 'team_documentation'
  | 'top_employee';

export interface ProjectHealth {
  id: string;
  name: string;
  status: string;
  organization_id: string;
  organization_name: string;
  total_budget_hours: number;
  actual_hours: number;
  remaining_hours: number;
  utilization_pct: number;
  open_tickets: number;
  total_tickets: number;
  completed_milestones: number;
  total_milestones: number;
  end_date?: string;
  days_until_deadline?: number;
}

export interface DashboardWidget {
  id: string;
  dashboard_id: string;
  widget_type: WidgetType;
  title?: string;
  layout_x: number;
  layout_y: number;
  layout_w: number;
  layout_h: number;
  config: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface Dashboard {
  id: string;
  user_id: string;
  organization_id: string;
  name: string;
  is_default: boolean;
  sort_order: number;
  config: Record<string, any>;
  widgets?: DashboardWidget[];
  created_at: string;
  updated_at: string;
}

export interface WidgetCatalogItem {
  type: WidgetType;
  name_key: string;
  description_key: string;
  default_w: number;
  default_h: number;
  min_w: number;
  min_h: number;
  max_w: number;
  max_h: number;
  roles: UserRole[];
  data_endpoint: string;
  configurable_filters: string[];
  /** Drill-down list route; `{field}` is filled from the clicked row. */
  drilldown_route?: string;
}

// ==========================================
// Contact CRM Types
// ==========================================

export type InteractionType = 'call' | 'email' | 'meeting' | 'note';
export type LocationType = 'office' | 'home' | 'client' | 'other';

/**
 * The two portal ranks an `portal_access` grant may carry (Stage יז, t2).
 *
 * Before t2 `portal_role` was descriptive — stored and returned but read by no
 * authorization conditional. It is now ENFORCED, and these are the only two
 * values the server accepts. The pre-t2 default `customer_viewer` still exists
 * on legacy grants and is treated as the LOWEST rank, never above it.
 */
export type PortalRole = 'customer_portal_manager' | 'customer_employee';

export interface Contact {
  id: string;
  organization_id: string;
  organization_name?: string;
  name: string;
  email?: string;
  phone?: string;
  mobile?: string;
  role_title?: string;
  notes?: string;
  /** P-01/A3 — the contact's business role (never an access grant). */
  contact_role?: 'customer_manager' | 'primary_contact' | 'customer_employee' | null;
  is_primary: boolean;
  /** Holds an active portal grant marked "מאשר קריאות" (contacts list only). */
  is_ticket_approver?: boolean;
  /** P-18 (D-136): the contact's portal grant, shown read-only on the contact. */
  portal_access_id?: string | null;
  portal_access_active?: boolean | null;
  portal_access_role?: string | null;
  /** P-42 (D-207 Q-191): the login (users.id) of that grant — a contact with access can be picked into a discussion. */
  portal_access_user_id?: string | null;
  portal_access_approve_tickets?: boolean;
  /** P-18 (D-134): may the caller edit this contact (GM or the customer's Account Manager). */
  can_edit?: boolean;
  active: boolean;
  created_by?: string;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

/**
 * The customer-side access choice offered when a contact is given portal
 * access. The server maps these three DOWN to the two canonical `portal_role`
 * ranks; the enum of three is only ever a UI concept and is never stored
 * verbatim.
 */
export type ContactAccessType = 'customer_manager' | 'primary_contact' | 'customer_employee';

export interface ContactInteraction {
  id: string;
  contact_id: string;
  interaction_type: InteractionType;
  summary?: string;
  ticket_id?: string;
  ticket_title?: string;
  created_by?: string;
  created_by_name?: string;
  created_at: string;
}

export const getTaskTypeLabel = (type: TaskType, t: TFunction): string =>
  t(`taskType.${type}`);

export const getTaskTypeLabels = (t: TFunction): Record<TaskType, string> => ({
  ticket: t('taskType.ticket'),
  task: t('taskType.task'),
  discussion: t('taskType.discussion'),
  milestone: t('taskType.milestone'),
});

export const getLocationTypeLabel = (type: LocationType, t: TFunction): string =>
  t(`locationType.${type}`);

export const getLocationTypeLabels = (t: TFunction): Record<LocationType, string> => ({
  office: t('locationType.office'),
  home: t('locationType.home'),
  client: t('locationType.client'),
  other: t('locationType.other'),
});

export const getInteractionTypeLabel = (type: InteractionType, t: TFunction): string =>
  t(`interactionType.${type}`);

export const getInteractionTypeLabels = (t: TFunction): Record<InteractionType, string> => ({
  call: t('interactionType.call'),
  email: t('interactionType.email'),
  meeting: t('interactionType.meeting'),
  note: t('interactionType.note'),
});

// ==========================================
// Lead Pipeline Types
// ==========================================

// P-51 (D-224 s.2): characterization_scheduled ("נקבעה פגישת אפיון", P-38a) left the lead (v52).
export type LeadStatus = 'new' | 'contacted' | 'meeting_scheduled' | 'qualified' | 'proposal_sent' | 'converted' | 'lost';
export type LeadSource = 'facebook' | 'instagram' | 'linkedin' | 'google' | 'website' | 'referral' | 'phone' | 'email' | 'whatsapp' | 'other';
export type CallStatus = 'answered' | 'no_answer' | 'callback' | 'meeting' | 'email_sent' | 'whatsapp';

export interface Lead {
  id: string;
  company_name: string;
  contact_name: string | null;
  tax_id: string | null;
  email: string | null;
  phone: string | null;
  address: Record<string, any>;
  lead_quality: number;
  lead_status: LeadStatus;
  lead_source: LeadSource | null;
  loss_reason: string | null;
  total_deal_value: number;
  converted_to_org_id: string | null;
  converted_from_quote_id: string | null;
  converted_at: string | null;
  assigned_to: string | null;
  assigned_to_name?: string;
  next_follow_up: string | null;
  notes: string | null;
  created_by: string | null;
  created_by_name?: string;
  /** FQ-022: computed by the list query (latest of the lead's own update, meetings, quotes, documentation, history). */
  last_activity_at?: string;
  /** FQ-022: an open lead with no activity for 14 days ("לא טופל"). */
  is_stale?: boolean;
  products?: LeadProduct[];
  communications?: LeadCommunication[];
  created_at: string;
  updated_at: string;
}

export interface LeadProduct {
  id: string;
  lead_id: string;
  product_id: string | null;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  vat_rate: number;
  vat_amount: number;
  notes: string | null;
  created_at: string;
}

export interface LeadCommunication {
  id: string;
  lead_id: string;
  communication_date: string;
  call_status: CallStatus;
  next_communication: string | null;
  summary: string | null;
  created_by: string | null;
  created_by_name?: string;
  created_at: string;
}

export interface Product {
  id: string;
  name: string;
  description: string | null;
  sku: string | null;
  unit_price: number;
  category: string | null;
  vat_rate: number;
  active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export type QuoteType = 'characterization' | 'development' | 'service' | 'hour_bank' | 'lll' | 'project' | 'maintenance' | 'custom';
/**
 * Quote lifecycle (decision 48). Legacy `approved`/`converted` no longer exist —
 * the backend migrated them to `accepted`. `superseded` = replaced by a newer
 * revision (decision 47).
 */
export type QuoteStatus =
  | 'draft'
  | 'sent'
  | 'viewed'
  | 'accepted'
  | 'rejected'
  | 'expired'
  | 'cancelled'
  | 'superseded';

export interface Quote {
  id: string;
  organization_id: string | null;
  organization_name?: string;
  lead_id: string | null;
  lead_company_name?: string;
  /** FIX-B35 (QX-651): GET /quotes/:id - a sent lead quote whose e-mail did not leave. */
  lead_mail_pending?: boolean;
  project_id: string | null;
  quote_number: number;
  quote_type: QuoteType;
  title: string;
  description: string | null;
  status: QuoteStatus;
  server_cost: number;
  total_amount: number;
  vat_total: number;
  grand_total: number;
  valid_until: string | null;
  sent_at: string | null;
  /** Legacy columns (kept by the backend for history). Prefer `accepted_at`. */
  approved_at?: string | null;
  approved_by?: string | null;
  rejection_reason: string | null;
  document_url: string | null;
  notes: string | null;
  created_by: string;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
  // Revision chain + lifecycle timestamps (u4 sales)
  revision_number?: number;
  parent_quote_id?: string | null;
  root_quote_id?: string | null;
  superseded_by?: string | null;
  viewed_at?: string | null;
  accepted_at?: string | null;
  accepted_by?: string | null;
  rejected_at?: string | null;
  expired_at?: string | null;
  cancelled_at?: string | null;
  cancel_reason?: string | null;
  signed_at?: string | null;
  superseded_at?: string | null;
  line_items?: QuoteLineItem[];
  // Detail-only (GET /quotes/:id)
  events?: QuoteEvent[];
  revisions?: QuoteRevisionRef[];
  lifecycle?: QuoteLifecycle;
  contracts?: { id: string; title: string; status: ContractStatus }[];
  engagements?: { id: string; type: EngagementType; name: string | null }[];
}

export interface QuoteLineItem {
  id: string;
  quote_id: string;
  sort_order: number;
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
  vat_rate: number;
  vat_amount: number;
  notes: string | null;
  created_at: string;
}

export const getLeadStatusLabel = (status: LeadStatus, t: TFunction): string =>
  t(`leadStatus.${status}`);

export const getLeadStatusLabels = (t: TFunction): Record<LeadStatus, string> => ({
  new: t('leadStatus.new'),
  contacted: t('leadStatus.contacted'),
  meeting_scheduled: t('leadStatus.meeting_scheduled'),
  qualified: t('leadStatus.qualified'),
  proposal_sent: t('leadStatus.proposal_sent'),
  converted: t('leadStatus.converted'),
  lost: t('leadStatus.lost'),
});

export const getLeadSourceLabel = (source: LeadSource, t: TFunction): string =>
  t(`leadSource.${source}`);

export const getLeadSourceLabels = (t: TFunction): Record<LeadSource, string> => ({
  facebook: t('leadSource.facebook'),
  instagram: t('leadSource.instagram'),
  linkedin: t('leadSource.linkedin'),
  google: t('leadSource.google'),
  website: t('leadSource.website'),
  referral: t('leadSource.referral'),
  phone: t('leadSource.phone'),
  email: t('leadSource.email'),
  whatsapp: t('leadSource.whatsapp'),
  other: t('leadSource.other'),
});

export const getCallStatusLabel = (status: CallStatus, t: TFunction): string =>
  t(`callStatus.${status}`);

export const getCallStatusLabels = (t: TFunction): Record<CallStatus, string> => ({
  answered: t('callStatus.answered'),
  no_answer: t('callStatus.no_answer'),
  callback: t('callStatus.callback'),
  meeting: t('callStatus.meeting'),
  email_sent: t('callStatus.email_sent'),
  whatsapp: t('callStatus.whatsapp'),
});

export const getQuoteTypeLabel = (type: QuoteType, t: TFunction): string =>
  t(`quoteType.${type}`);

export const getQuoteTypeLabels = (t: TFunction): Record<QuoteType, string> => ({
  characterization: t('quoteType.characterization'),
  development: t('quoteType.development'),
  service: t('quoteType.service'),
  hour_bank: t('quoteType.hour_bank'),
  lll: t('quoteType.lll'),
  project: t('quoteType.project'),
  maintenance: t('quoteType.maintenance'),
  custom: t('quoteType.custom'),
});

export const getQuoteStatusLabel = (status: QuoteStatus, t: TFunction): string =>
  t(`quoteStatus.${status}`);

export const getQuoteStatusLabels = (t: TFunction): Record<QuoteStatus, string> => ({
  draft: t('quoteStatus.draft'),
  sent: t('quoteStatus.sent'),
  viewed: t('quoteStatus.viewed'),
  accepted: t('quoteStatus.accepted'),
  rejected: t('quoteStatus.rejected'),
  expired: t('quoteStatus.expired'),
  cancelled: t('quoteStatus.cancelled'),
  superseded: t('quoteStatus.superseded'),
});

// ==========================================
// Backup & Restore Types
// ==========================================

export type BackupStatus = 'running' | 'success' | 'failed' | 'partial' | 'verifying' | 'uploading';
export type BackupTriggerType = 'scheduled' | 'manual' | 'pre_restore';
export type StorageProviderType = 'local' | 's3' | 'google_drive';

export interface BackupRecord {
  id: string;
  schedule_id?: string;
  schedule_name?: string;
  storage_config_id?: string;
  storage_name?: string;
  status: BackupStatus;
  trigger_type: BackupTriggerType;
  triggered_by?: string;
  triggered_by_name?: string;
  include_database: boolean;
  include_attachments: boolean;
  include_audio: boolean;
  include_config: boolean;
  include_logs: boolean;
  local_path?: string;
  remote_path?: string;
  remote_provider?: StorageProviderType;
  database_size_bytes?: number;
  attachments_size_bytes?: number;
  audio_size_bytes?: number;
  config_size_bytes?: number;
  logs_size_bytes?: number;
  total_size_bytes?: number;
  database_hash?: string;
  verified_at?: string;
  verified_restorable?: boolean;
  encrypted: boolean;
  database_version?: string;
  table_count?: number;
  row_counts?: Record<string, number>;
  error_message?: string;
  log?: string;
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
  created_at: string;
}

export interface BackupSchedule {
  id: string;
  name: string;
  cron_expression: string;
  storage_config_id?: string;
  storage_name?: string;
  storage_provider?: StorageProviderType;
  include_database: boolean;
  include_attachments: boolean;
  include_audio: boolean;
  include_config: boolean;
  include_logs: boolean;
  encryption_enabled: boolean;
  retention_days: number;
  max_backups: number;
  active: boolean;
  last_run_at?: string;
  next_run_at?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface BackupStorageConfig {
  id: string;
  name: string;
  provider: StorageProviderType;
  config: Record<string, any>;
  is_default: boolean;
  active: boolean;
  last_tested_at?: string;
  last_test_success?: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface BackupStats {
  successful_count: number;
  failed_count: number;
  running_count: number;
  last_successful_backup?: string;
  total_storage_used: number;
  avg_duration_seconds?: number;
  active_schedules: number;
}

export const getStorageProviderLabel = (provider: StorageProviderType, t: TFunction): string =>
  t(`backups.provider_${provider}`);

export const getBackupStatusLabel = (status: BackupStatus, t: TFunction): string =>
  t(`backups.status_${status}`);

export const getBackupTriggerLabel = (trigger: BackupTriggerType, t: TFunction): string =>
  t(`backups.trigger_${trigger}`);

// ==========================================
// Bug Report Types
// ==========================================

/** D-146 (Q-102) — tickets.source (v30 CHECK; utils/ticketStatus.shared.ts TICKET_SOURCES). */
export type TicketSource = 'manual' | 'bug_report' | 'integration' | 'email' | 'phone' | 'whatsapp' | 'meeting' | 'portal' | 'extension';

export interface BugReportContext {
  id: string;
  ticket_id: string;
  session_id?: string;
  trace_ids: string[];
  console_log: any[];
  network_log: any[];
  breadcrumbs: any[];
  performance_data: Record<string, any>;
  browser_info: Record<string, any>;
  app_info: Record<string, any>;
  screenshot_path?: string;
  recording_path?: string;
  agent_summary?: string;
  auto_tags: string[];
  // D-323
  report_status?: BugReportStatus;
  verdict?: BugReportVerdict | null;
  decision_ref?: string | null;
  triage?: BugReportTriage;
  report_kind?: BugReportKind;
  /** D-341: the manager's note to Claude (manager only). */
  claude_note?: string | null;
  status_history?: Array<{ status: BugReportStatus; at: string; by: string | null; note?: string; verdict?: string | null; kind?: string }>;
  full_screenshot_path?: string | null;
  audio_path?: string | null;
  region?: { x: number; y: number; w: number; h: number } | null;
  page_url?: string | null;
  page_title?: string | null;
  created_at: string;
  updated_at: string;
}

/** D-323: the reporter-visible lifecycle of a bug report. */
export type BugReportStatus = 'new' | 'reviewed' | 'fixing' | 'fixed_local' | 'approved_cloud' | 'in_cloud' | 'closed' | 'answered' | 'handled' | 'wont_fix';
export type BugReportTriage = 'pending' | 'claude' | 'self';
/** D-341: a bug (with a screen capture) or an improvement suggestion (no capture). */
export type BugReportKind = 'bug' | 'suggestion';
export type BugReportVerdict = 'bug' | 'not_bug' | 'duplicate' | 'change_request';
export const BUG_REPORT_STATUS_ORDER: BugReportStatus[] = ['new', 'reviewed', 'fixing', 'fixed_local', 'approved_cloud', 'in_cloud', 'closed', 'answered', 'handled', 'wont_fix'];
/** D-341: the manager's four simple choices in the treatment panel (the rest are set by the review / fix flow). */
export const BUG_REPORT_MANAGER_STATUSES: BugReportStatus[] = ['new', 'fixing', 'handled', 'wont_fix'];
export const BUG_REPORT_VERDICTS: BugReportVerdict[] = ['bug', 'not_bug', 'duplicate', 'change_request'];

/** One row of "הדיווחים שלי". */
export interface MyBugReport {
  id: string;
  title: string;
  description?: string | null;
  created_at: string;
  report_status: BugReportStatus;
  report_kind?: BugReportKind;
  verdict?: BugReportVerdict | null;
  decision_ref?: string | null;
  status_history: Array<{ status: BugReportStatus; at: string; by: string | null; note?: string; verdict?: string | null }>;
  page_title?: string | null;
  page_url?: string | null;
  has_screenshot: boolean;
  has_audio: boolean;
}

export type PerformanceRating = 'good' | 'needs_attention' | 'poor';

export interface CreateBugReportPayload {
  /** D-341: 'bug' (default) or 'suggestion' (no screen capture). */
  report_kind?: BugReportKind;
  title?: string;
  description?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  has_audio?: boolean;
  region?: { x: number; y: number; w: number; h: number } | null;
  page_url?: string;
  page_title?: string;
  entity_type?: string;
  entity_id?: string;
  steps_to_reproduce?: string;
  expected_behavior?: string;
  actual_behavior?: string;
  capture_snapshot?: Record<string, any>;
  project_id?: string;
  milestone_id?: string;
  contact_id?: string;
}

export interface UpdateBugReportPayload {
  steps_to_reproduce?: string;
  expected_behavior?: string;
  actual_behavior?: string;
  agent_summary?: string;
  auto_tags?: string[];
}

export interface AgentReadableContext {
  summary: {
    error_count: number;
    warning_count: number;
    failed_requests: number;
    performance_rating: PerformanceRating;
    session_duration_ms: number;
  };
  errors: {
    message: string;
    count: number;
    first_seen: string;
    last_seen: string;
    stack_preview?: string;
  }[];
  failed_requests: {
    method: string;
    url: string;
    status: number;
    error_type: string;
    timestamp: string;
  }[];
  recent_actions: {
    type: string;
    target?: string;
    route?: string;
    timestamp: string;
  }[];
  environment: {
    browser: string;
    os: string;
    app_version: string;
    url: string;
    route: string;
  };
  performance: {
    metric: string;
    value: number;
    rating: PerformanceRating;
  }[];
}

export interface BugReportListItem {
  id: string;
  title: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: string;
  created_at: string;
  organization_id: string;
  organization_name: string;
  reporter_name: string;
  agent_name?: string;
  customer_id: string;
  assigned_agent_id?: string;
  auto_tags: string[];
  has_screenshot: boolean;
  has_recording: boolean;
  performance_rating: PerformanceRating;
  report_status?: BugReportStatus;
  triage?: BugReportTriage;
  report_kind?: BugReportKind;
  verdict?: BugReportVerdict | null;
  decision_ref?: string | null;
  page_title?: string | null;
  has_audio?: boolean;
  has_full_screenshot?: boolean;
}

// ==========================================
// Permissions, Portal Access & Employee Profiles
// ==========================================

export type PermissionName =
  | 'canViewCustomer'
  | 'canViewProject'
  | 'canViewDevelopment'
  | 'canViewMilestone'
  | 'canViewTask'
  | 'canViewDocument'
  | 'canViewFolder'
  | 'canViewProfitability'
  | 'canViewEmployeeCost'
  | 'canGrantPermission';

export type PermissionEffect = 'allow' | 'deny';
export type EmploymentType = 'hourly' | 'global';

/** Screen permissions (permissions brief, 2026-10-07): `GET /api/permissions/catalog`. */
export interface PermissionCatalogItem {
  key: string;
  labelKey?: string;
  section?: string;
  defaultRoles: string[];
  /** The server keeps the screen for the General Manager whatever a grant says (newer servers). */
  serverAdminOnly?: boolean;
}

export interface PermissionGroup {
  id: string;
  name: string;
  description?: string | null;
  memberCount: number;
}

export interface PermissionGroupMember {
  userId: string;
  name: string;
  email?: string | null;
  role?: string | null;
}

export type PermissionSubjectType = 'group' | 'user';
export type PermissionGrantEffect = 'allow' | 'deny';

export interface PermissionGrant {
  key: string;
  effect: PermissionGrantEffect;
}

/** D-335 — an employee's own page override ("לפי עובד"). */
export type ScreenOverrideLevel = 'hidden' | 'view' | 'edit';

/** D-335 — one screen of one employee: `GET /api/permissions/overrides?userId=`. */
export interface ScreenOverrideRow {
  key: string;
  labelKey: string;
  section: string;
  /** The permissions screen: only the OTE manager, never overridden. */
  locked: boolean;
  /** Open without his own override (the role, the groups, the personal grants). */
  defaultOpen: boolean;
  /** FIX-PERMS-2 (Q2): "hidden" removes the menu item only; the screen's API is shared. Absent on an older server. */
  hiddenMenuOnly?: boolean;
  override: ScreenOverrideLevel | null;
  updatedAt: string | null;
  /** FIX-PERMS-SCOPE: the page has a "mine / every employee" mode, so the row has a second choice, "היקף". Absent on an older server. */
  scopeable?: boolean;
  /** FIX-PERMS-SCOPE: 'all' = every active employee, read only, no money; 'role' = the role's scope; null = no scope choice. */
  scope?: ScreenDataScope | null;
}

/** FIX-PERMS-SCOPE — the data scope of a page for one employee. */
export type ScreenDataScope = 'all' | 'role';

export interface ScreenOverridesOfUser {
  userId: string;
  role: string;
  screens: ScreenOverrideRow[];
}

export interface PermissionRule {
  id: string;
  role: string;
  permission: PermissionName;
  scope: string;
  entity_type?: string;
  field?: string;
  effect: PermissionEffect;
  created_at: string;
}

export interface PortalAccess {
  id: string;
  user_id: string;
  organization_id: string;
  contact_id?: string;
  /**
   * A canonical `PortalRole` on grants created since t2. Typed loosely because
   * a LEGACY grant may still carry the pre-t2 `customer_viewer`, which the
   * server ranks at the floor — narrowing this to `PortalRole` would assert a
   * guarantee the data does not yet make. Read it through a fallback.
   */
  portal_role: PortalRole | 'customer_viewer' | string;
  permissions: Record<string, any>;
  active: boolean;
  created_at: string;
  updated_at: string;
  // joined fields
  user_name?: string;
  user_email?: string;
  organization_name?: string;
  contact_name?: string;
  /** The linked contact's business role — the "access type". NULL = unclassified. */
  contact_role?: string | null;
  /** The login's `users.role` — `admin` marks a senior employee grant. */
  user_role?: string;
  /** The login's `users.active` — the status of an EMPLOYEE grant (spec §7.1). */
  user_active?: boolean;
  /** D-153 (a): the customer's OTE-side manager (`organizations.account_manager_id`). */
  account_manager_id?: string | null;
  account_manager_name?: string | null;
  /** P-23 (D-157): who created the grant - 'customer' = the customer's own manager. */
  created_via?: 'ote' | 'customer' | string;
  /** W-07: a customer employee created WITHOUT a contact on purpose (not a stuck row). */
  no_contact?: boolean;
}

/** P-13 (D-108) — the employment-details object on an employee profile. */
export interface EmploymentInfo {
  details?: string | null;
  documents_folder_url?: string | null;
  [key: string]: unknown;
}

/** P-13 (D-108) — one version of an employee's cost (employee_cost_history). */
export interface EmployeeCostVersion {
  id: string;
  hourly_cost: string | number | null;
  monthly_cost: string | number | null;
  work_hours_per_day: string | number | null;
  work_days: number[] | null;
  effective_from: string;
  effective_to: string | null;
  reason: string | null;
  created_at: string;
  created_by_name: string | null;
}

export interface EmployeeProfile {
  id: string;
  user_id: string;
  /**
   * The employee's ORGANIZATION. Server-side only: it is NOT NULL and carries
   * every cross-organization authorization gate, but it is neither displayed
   * nor editable on the Employee screen (spec §3.7.1) and is therefore absent
   * from this contract.
   */
  employment_type: EmploymentType;
  hourly_cost?: number;
  monthly_cost?: number;
  work_hours_per_day?: number;
  work_days?: number[];
  /**
   * P-13 (D-108) — employment details: `details` ("מידע העסקה", free text) and
   * `documents_folder_url` (link to the employee's documents folder). Other
   * keys are kept as they are. OTE manager only (masked otherwise).
   */
  employment_info?: EmploymentInfo | null;
  /** P-13 (D-108) — YYYY-MM-DD. OTE manager only. */
  employment_start_date?: string | null;
  /** P-33c (D-185) — internal-work-only employee ("חני"). Written by PATCH/POST employee-profiles (P-33b). */
  internal_only?: boolean;
  /** P-13 (D-109) — no cost in effect today ("חסרה עלות"). OTE manager only. */
  cost_missing?: boolean | null;
  /** D-305 — travel allowance per office day / monthly cap (NIS, numeric strings). OTE manager only; never sent to the employee. */
  travel_per_day?: string | number | null;
  travel_monthly_cap?: string | number | null;
  /** D-306 — Israeli ID (9 digits) and second phone. OTE manager + the employee themself. */
  id_number?: string | null;
  phone_secondary?: string | null;
  /**
   * Employment status as a BUSINESS fact (Stage יז, t2) — deliberately
   * separate from `users.active`, which is whether the login may sign in.
   * Setting this FALSE also disables the login, atomically and one-way:
   * reactivating the employee does NOT restore revoked access.
   */
  active: boolean;
  created_at: string;
  updated_at: string;
  // joined fields
  user_name?: string;
  user_email?: string;
  user_role?: string;
  /**
   * FALSE when this row is an OTE login identity that has no employee profile
   * yet (the roster is identity-backed, so such a person is still listed and
   * can be given a profile from the UI). Never present on a saved profile.
   */
  has_profile?: boolean;
  /**
   * The EMPLOYEE's OWN business name (Stage יז, t5). Since t5 the employee
   * owns its identity, so this is populated even for a login-less employee —
   * `user_name` alone would render such a row nameless. NULL only on a bare
   * staff identity (the roster's `has_profile=false` side).
   */
  name?: string;
  /** The employee's own business e-mail (t5). Not an authentication credential. */
  email?: string;
  /**
   * The EMPLOYEE's own contact details (t7). Canonical employee data, stored on
   * `employee_profiles` and independent of any login — they are the business
   * record's own fields, so a login-less employee carries them too.
   */
  phone?: string;
  address?: string;
  notes?: string;
  /** D-148 (Q-105) — "תפקיד", free text. */
  job_title?: string | null;
  /** The login's own status (users.active); null for a login-less employee. */
  identity_active?: boolean | null;
  /** D-148 (Q-109) — an optional photo, served by GET /employee-profiles/:id/photo. */
  has_photo?: boolean;
  photo_updated_at?: string | null;
  /** D-148 (Q-106) — the system-roles summary of the roster row. */
  is_ote_manager?: boolean;
  /** D-322 (7) — at least one device of the login approved browser notices. */
  has_push_subscription?: boolean;
  account_manager_count?: number;
  project_manager_count?: number;
  team_lead_count?: number;
}

/** P-20 (D-148) — one link of the "תפקידים במערכת" area. */
export interface EmployeeRoleLink { id: string; name: string; organization_name?: string | null }

/** P-20 (D-148) — GET /employee-profiles/:id/system-roles. */
export interface EmployeeSystemRoles {
  has_login: boolean;
  is_ote_manager: boolean;
  account_manager_of: EmployeeRoleLink[];
  project_manager_of: EmployeeRoleLink[];
  team_lead_of: EmployeeRoleLink[];
}

export type EmployeeRoleKind = 'account-manager' | 'project-manager' | 'team-lead';

/** P-20 (D-148 / Q-108) — what the deactivation window shows. */
export interface EmployeeDeactivationPreview {
  employee: { id: string; user_id: string | null; name: string; active: boolean };
  has_login: boolean;
  is_ote_manager: boolean;
  is_last_ote_manager: boolean;
  is_self: boolean;
  open_tasks: Array<{ id: string; title: string; status: string; task_type: string | null; organization_name: string | null; is_responsible: boolean }>;
  active_timers: Array<{ id: string; ticket_id: string; ticket_title: string | null; status: string }>;
  system_roles: EmployeeSystemRoles;
  active_employees: Array<{ user_id: string; name: string }>;
}

export interface EmployeeDeactivateBody {
  task_transfers?: Array<{ ticket_id: string; to_user_id: string }>;
  role_transfers?: {
    account_manager?: Array<{ organization_id: string; to_user_id: string }>;
    project_manager?: Array<{ project_id: string; to_user_id: string | null }>;
    team_lead?: Array<{ project_id: string; to_user_id: string | null }>;
  };
  reason?: string | null;
}

// ==========================================
// Engagement Types (Stage C)
// ==========================================

export type EngagementType = 'project' | 'development' | 'service_contract' | 'hour_bank' | 'service_without_contract';
export type EngagementStatus = 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';

export interface EngagementChild {
  id: string;
  engagement_id: string;
  organization_id: string;
  name: string;
  status: EngagementStatus;
  description?: string;
  start_date?: string;
  end_date?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
}

/**
 * P-03/B, D6 — the commercial terms of a service contract / hour bank / לל"ח
 * (create: required per type; edit: any subset).
 */
export interface TypedEngagementTerms {
  start_date?: string | null;
  end_date?: string | null;
  payment_terms?: string | null;
  monthly_quota_hours?: number | null;
  monthly_price?: number | null;
  overtime_hourly_rate?: number | null;
  server_monthly_fee?: number | null;
  server_monthly_cost?: number | null;
  overage_policy?: 'charge' | 'approval';
  approval_policy?: 'none' | 'per_task' | 'threshold';
  approval_threshold_hours?: number | null;
  approved_extra_hours?: number | null;
  purchased_hours?: number | null;
  purchase_price?: number | null;
  internal_hourly_rate?: number | null;
  expiry_date?: string | null;
  rate?: number | null;
}

/** Parent engagement row as returned by the list/get endpoints (with attached typed child). */
export interface Engagement {
  id: string;
  type: EngagementType;
  organization_id: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  /** D-265 sec.2 (v67): archived with everything under it (restore: OTE manager only). */
  archived_at?: string | null;
  // joined fields
  organization_name: string;
  child_name: string | null;
  child_status: EngagementStatus | null;
  child_id: string | null;
  /** P-02b — the projects row of a project / development engagement (null otherwise). */
  project_id?: string | null;
}

// ------------------------------------------------------------------
// Stage ח — Service / Hour Bank / לל"ח enriched child types
// ------------------------------------------------------------------

// P-03/A1 (D-31): by_10..by_25 / net..net_90 is the list a new value comes
// from; the legacy codes may still be stored on older rows.
export type PaymentTerms =
  | 'by_10' | 'by_15' | 'by_20' | 'by_25'
  | 'net' | 'net_30' | 'net_60' | 'net_90'
  | 'net_10' | 'net_15' | 'net_20' | 'net_25'
  | 'current' | 'current_30' | 'current_60' | 'current_90';

export interface ServiceContract extends EngagementChild {
  monthly_quota_hours?: number | null;
  monthly_price?: number | null;
  overtime_hourly_rate?: number | null;
  billing_day?: number | null;
  /** P-03/A2 — "חיוב שרת חודשי (₪)". */
  server_monthly_fee?: number | null;
  server_monthly_cost?: number | null;
  payment_terms?: PaymentTerms | null;
  sla_response_hours?: number | null;
  sla_resolution_hours?: number | null;
  approved_extra_hours?: number;
  approved_extra_hours_expires_at?: string | null;
}

export interface HourBank extends EngagementChild {
  purchased_hours?: number | null;
  hourly_rate?: number | null;
  internal_hourly_rate?: number | null;
  expiry_date?: string | null;
}

export interface ServiceWithoutContract extends EngagementChild {
  rate?: number | null;
  payment_terms?: PaymentTerms | null;
}

/** Derived hour-bank snapshot returned by the API. */
export interface HourBankSnapshot {
  id: string;
  engagement_id: string;
  organization_id: string;
  name: string;
  status: EngagementStatus;
  purchased_hours: number | null;
  used_hours: number;
  balance: number;
  utilization_pct: number;
  hourly_rate: number | null;
  internal_hourly_rate: number | null;
  start_date?: string | null;
  expiry_date?: string | null;
}

export interface HourBankUsage {
  id: string;
  hour_bank_id: string;
  ticket_id?: string | null;
  agent_id?: string | null;
  used_on: string;
  hours: number;
  description?: string | null;
  source: 'worklog' | 'manual' | 'adjustment' | 'auto';
  created_at: string;
  override_reason?: string | null;
  /** EQ-012: true = system flag (locked, waiting for a new bank), not a human approval. */
  is_system_flag?: boolean;
  /** EQ-014: the locked part of this row, in minutes (0 = none); computed on the server. */
  locked_minutes?: number;
  /** D-287: used_on of the row marked as the overrun (the explanation next to the tag), or null. */
  locked_origin_date?: string | null;
}

export interface HourBankAdjustment {
  id: string;
  hour_bank_id: string;
  direction: 'credit' | 'debit';
  amount_hours: number;
  reason: string;
  created_by: string;
  created_at: string;
}

export interface HourBankReport {
  id: string;
  hour_bank_id: string;
  period_start: string;
  period_end: string;
  totals: Record<string, unknown>;
  email_to?: string | null;
  portal_path?: string | null;
  reports_folder?: string | null;
  sent_at: string;
}

export type ServiceLogReviewStatus =
  | 'pending_employee' | 'employee_approved' | 'ote_approved' | 'report_emitted';

export interface ServiceLogReview {
  id: string;
  service_contract_id: string;
  month: string;
  status: ServiceLogReviewStatus;
  customer_id: string;
  generated_at?: string | null;
  employee_approved_at?: string | null;
  ote_approved_at?: string | null;
  ote_approved_by?: string | null;
  report_emitted_at?: string | null;
  report_email_to?: string | null;
  report_portal_path?: string | null;
  report_folder_path?: string | null;
}

export interface ServiceLogReviewEntry {
  id: string;
  review_id: string;
  agent_id: string;
  agent_name?: string;
  ticket_id?: string | null;
  task_title?: string | null;
  description?: string | null;
  hours: number;
  work_date?: string | null;
  employee_confirmed_at?: string | null;
}

// P-10c (D-70) — the monthly service-log approval process.
export type ServiceLogCustomerStatus = 'awaiting_employees' | 'awaiting_manager' | 'sent';

/** A service contract's place in the month's process (its tab). */
export interface ServiceLogContractProcess {
  customer_status: ServiceLogCustomerStatus | null;
  report_emitted_at: string | null;
  employees: { agent_id: string; agent_name: string | null; approved_at: string | null; hours: number }[];
}

export interface ServiceLogBreakdownTask {
  ticket_id: string | null;
  title: string;
  engagement_id: string;
  contract_name: string;
  hours: number;
}

/** The employee's own breakdown for a month, by customer. */
export interface ServiceLogEmployeeBreakdown {
  month: string;
  approval: {
    notified_at: string | null;
    approved_at: string | null;
    reopened_at: string | null;
    reopen_reason: string | null;
  } | null;
  customers: {
    organization_id: string;
    organization_name: string;
    tasks: ServiceLogBreakdownTask[];
    total_hours: number;
    /** EQ-016 (Q1): this customer was already approved and sent — shown closed. */
    sent?: boolean;
  }[];
  total_hours: number;
}

export interface ServiceLogEmployeeMonth {
  agent_id: string;
  agent_name: string | null;
  notified_at: string | null;
  last_reminded_at: string | null;
  reminder_count: number;
  approved_at: string | null;
  reopened_at: string | null;
  reopen_reason: string | null;
  total_hours: number;
  /** QA-10c #11: approved, and still on a customer not sent — a re-open has something to open. */
  reopenable?: boolean;
}

export interface ServiceLogCustomerSummary {
  organization_id: string;
  organization_name: string;
  status: ServiceLogCustomerStatus;
  review_ids: string[];
  engagement_ids: string[];
  tasks: {
    ticket_id: string | null;
    title: string;
    engagement_id: string;
    contract_name: string;
    /** D-76: `approved` false → "⚠️ X לא אישר" next to the task. */
    employees: { agent_id: string; agent_name: string | null; hours: number; approved: boolean }[];
    total_hours: number;
  }[];
  employees: { agent_id: string; agent_name: string | null; hours: number; approved: boolean }[];
  total_hours: number;
  report_emitted_at: string | null;
  report_email_to: string | null;
  /** QA-10c #10: who gets the breakdown by e-mail; null = no e-mail (portal only). */
  recipient_email?: string | null;
  /** FIX-SERVICE-TAB: the recipient contact's name (the send dialog). */
  recipient_name?: string | null;
  /** FIX-SERVICE-TAB (v104): the result of the customer e-mail; null = sent before the result was kept. */
  report_email_status?: 'pending' | 'sent' | 'failed' | 'skipped' | null;
  report_email_error?: string | null;
  report_email_attempted_at?: string | null;
}

/** The "אישורים חודשיים" page. */
export interface ServiceLogMonthOverview {
  month: string;
  closed: boolean;
  employees: ServiceLogEmployeeMonth[];
  customers: ServiceLogCustomerSummary[];
  all_employees_approved: boolean;
  manager_notified_at: string | null;
}

// ==========================================
// Stage י — Documents, foldering, versions,
// sharing & signatures (§38/§39/§40/§41)
// ==========================================

export type DocumentStatus = 'draft' | 'ready_to_sign' | 'sent' | 'signed' | 'locked';

export type DocumentType =
  | 'general'
  | 'quote'
  | 'proposal'
  | 'contract'
  | 'delivery'
  | 'characterization'
  | 'report'
  | 'checklist'
  | 'other';

export type DocumentFolderType =
  | 'general'
  | 'characterization'
  | 'delivery'
  | 'hours_reports'
  | 'customer_files'
  | 'contracts'
  | 'quotes'
  | 'general_documents';

/** Who may see a document (decision 56). Portal visibility additionally needs `is_published`. */
export type DocumentClassification = 'internal' | 'client_visible' | 'financial_sensitive';

export interface DocumentFolder {
  id: string;
  organization_id: string;
  parent_folder_id: string | null;
  name: string;
  folder_type: DocumentFolderType;
  description: string | null;
  /** System folders cannot be renamed, retyped or deleted. */
  is_system?: boolean;
  created_by: string | null;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
  document_count?: number;
  /** P-47: the lead this folder belongs to (kept as lineage after conversion). */
  lead_id?: string | null;
}

export interface DocumentVersion {
  id: string;
  document_id: string;
  version_number: number;
  file_name: string;
  mime_type: string | null;
  file_size: number;
  storage_path: string | null;
  version_note: string | null;
  hash_sha256: string | null;
  is_signed: boolean;
  uploaded_by: string | null;
  uploaded_by_name?: string;
  created_at: string;
}

export interface DocumentPermission {
  id: string;
  document_id: string;
  employee_id: string | null;
  user_id: string | null;
  can_view: boolean;
  can_edit: boolean;
  granted_by: string | null;
  created_at: string;
  grantee_name?: string;
}

export interface OteDocument {
  id: string;
  organization_id: string;
  organization_name?: string;
  folder_id: string | null;
  title: string;
  document_type: DocumentType;
  status: DocumentStatus;
  description: string | null;
  /** Derived server-side: client_visible AND published. Never sent by the client. */
  is_customer_visible: boolean;
  classification?: DocumentClassification;
  is_published?: boolean;
  published_at?: string | null;
  published_by?: string | null;
  signed_version_id: string | null;
  created_by: string | null;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
  folder_name?: string;
  /** P-47: the lead this document came from (kept as lineage after conversion). */
  lead_id?: string | null;
  /** DQ-207: the lead's name and whether the document's customer is the company itself (an open lead's document). */
  lead_name?: string | null;
  organization_is_internal?: boolean | null;
  versions?: DocumentVersion[];
  permissions?: DocumentPermission[];
  metadata?: Record<string, string>;
  latest_version?: DocumentVersion | null;
  links?: DocumentLink[];
  signatures?: DocumentSignature[];
  /** D-308c (DQ-292): the caller's rights on this document, from the server's enforced rule (list + bundle). */
  can_edit?: boolean;
  can_delete?: boolean;
  /** Bundle only: may grant / change / revoke shares. */
  can_share?: boolean;
}

export interface OteDocumentFolderInput {
  name: string;
  parent_folder_id?: string | null;
  folder_type?: DocumentFolderType;
  description?: string | null;
}

export interface OteDocumentInput {
  organization_id: string;
  folder_id?: string | null;
  title: string;
  document_type?: DocumentType;
  status?: DocumentStatus;
  description?: string | null;
  classification?: DocumentClassification;
}

export const getDocumentStatusLabel = (status: DocumentStatus, t: TFunction): string =>
  t(`documents.status_${status}`);

export const getDocumentStatusLabels = (t: TFunction): Record<DocumentStatus, string> => ({
  draft: t('documents.status_draft'),
  ready_to_sign: t('documents.status_ready_to_sign'),
  sent: t('documents.status_sent'),
  signed: t('documents.status_signed'),
  locked: t('documents.status_locked'),
});

export const getDocumentTypeLabel = (type: DocumentType, t: TFunction): string =>
  t(`documents.type_${type}`);

export const getDocumentFolderTypeLabel = (type: DocumentFolderType, t: TFunction): string =>
  t(`documents.folder_type_${type}`);

// ==========================================
// Stage י — Project delivery flow (§42) +
// customer testing period (§43)
// ==========================================

export type DeliveryStatus =
  | 'scope'
  | 'qa_checklist'
  | 'internal_qa'
  | 'customer_check'
  | 'customer_checklist'
  | 'delivery_contract'
  | 'delivered';

export interface ProjectDelivery {
  id: string;
  project_id: string | null;
  development_id: string | null;
  status: DeliveryStatus;
  scope_notes: string | null;
  internal_qa_notes: string | null;
  customer_check_notes: string | null;
  testing_period_start_date: string | null;
  testing_period_end_date: string | null;
  delivery_contract_document_id: string | null;
  delivered_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface DeliveryChecklistItem {
  id: string;
  delivery_id: string;
  checklist_type: 'qa' | 'customer';
  item_text: string;
  is_checked: boolean;
  checked_by: string | null;
  checked_at: string | null;
  sort_order: number;
  created_at: string;
}

export const getDeliveryStatusLabel = (status: DeliveryStatus, t: TFunction): string =>
  t(`deliveries.status_${status}`);

// ============================================================================
// Stage יא — Billing obligations / Morning / Debts (§44-§49)
// ============================================================================

export type ObligationType =
  | 'project' | 'development' | 'service_contract' | 'hour_bank'
  | 'service_without_contract' | 'other';

export type PaymentStatus = 'pending' | 'partial' | 'paid';

export interface Obligation {
  id: string;
  organization_id: string;
  engagement_id: string | null;
  obligation_type: ObligationType;
  title: string | null;
  description: string | null;
  amount: number;
  currency: string;
  issue_date: string;
  due_date: string | null;
  period_start: string | null;
  period_end: string | null;
  payment_terms: string | null;
  payment_status: PaymentStatus;
  external_accounting_reference: string | null;
  paid_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  // joined / derived
  organization_name?: string;
  items_count?: number;
  overdue?: boolean;
  balance?: number;
  items?: BillingItem[];
  events?: ObligationEvent[];
  // u3 — payments / adjustments / lock (derived money view)
  total?: number;
  adjustments_total?: number;
  paid_total?: number;
  outstanding?: number;
  locked?: boolean;
  locked_at?: string | null;
  payments?: import('./billing').Payment[];
  adjustments?: import('./billing').BillingAdjustment[];
}

export interface BillingItem {
  id: string;
  obligation_id: string;
  description: string | null;
  quantity: number | null;
  unit: string | null;
  rate: number | null;
  amount: number;
  /** P-14 (D-115) — the line's kind ('server', 'ai_units', 'hours', …). */
  line_kind?: string | null;
  source: 'worklog' | 'usage' | 'manual' | 'fixed';
  source_ref: string | null;
  item_date: string | null;
  created_at: string;
}

export interface ObligationEvent {
  id: string;
  obligation_id: string;
  event_type: 'created' | 'item_added' | 'morning_reported' | 'paid';
  note: string | null;
  ref_text: string | null;
  created_by: string | null;
  created_at: string;
}

export const getObligationTypeLabel = (type: ObligationType, t: TFunction): string =>
  t(`billing.type_${type}`);

// B.17 — manual task relationships.
export type TaskRelationType = 'related' | 'depends_on' | 'blocks';
export interface TaskRelation {
  id: string;
  relation_type: TaskRelationType; // from the perspective of the viewed task
  direction: 'outgoing' | 'incoming';
  created_at: string;
  created_by: string | null;
  other: { id: string; title: string; status: string; ticket_number: number | null; task_type: string };
}

// B.13 — controlled task status list.
export interface TaskStatusDef {
  key: string;
  label_he: string;
  label_en: string;
  category: 'open' | 'done';
  sort_order: number;
  active: boolean;
}

// B.21 — timers paused by a break, offered for resume when it ends.
export interface ResumableTimer {
  ticket_id: string;
  ticket_title: string;
}

export * from './sales';

/** P-10d (D-79 §5) — the KPI strip of the work-logs page (GET /work-logs/kpis). */
export interface WorkLogKpis {
  today_minutes: number;
  week_minutes: number;
  month_minutes: number;
  /** "מתחילת העבודה" — every log in scope. */
  total_minutes: number;
  /** Open work cycles in scope. */
  open_records: number;
  /** OTE manager only: employees with a running timer. */
  active_now?: number;
  /** SEC-2b (D-92, D-104) — the caller sees other employees' logs (manager / AM / PM / TL). */
  sees_others?: boolean;
}

/** P-10d (D-72) — one running timer in "פעילות עכשיו" (GET /work-logs/active-now). */
export interface ActiveNowEntry {
  id: string;
  user_id: string;
  agent_name: string | null;
  ticket_id: string;
  ticket_title: string | null;
  ticket_number: number | null;
  task_type: string | null;
  organization_id: string | null;
  organization_name: string | null;
  work_record_id: string | null;
  started_at: string;
  record_first_started_at: string | null;
}

/** P-10a (D-67) — a task the "התחל עבודה" window offers (GET /timer/tasks). */
export interface StartableTask {
  id: string;
  title: string;
  status: string;
  ticket_number: number | null;
  organization_id: string | null;
  organization_name: string | null;
  container_name: string | null;
  /** FU-19 — the task of an "אפיון" card (its container is "אפיון — <שם>"). */
  characterization_id?: string | null;
  milestone_name: string | null;
  last_worked_at: string | null;
  /** D-274b: the task sits "ישירות תחת OTE" (the hidden "עבודה פנימית" container) — the UI shows that label. */
  direct_under_ote?: boolean | null;
}

/** P-10a (D-64) — every part of one work record (GET /timer/records/:id/segments). */
export interface RecordSegments {
  work_record_id: string;
  ticket_id: string;
  segments: Array<{ id: string; start_time: string; end_time: string; duration_minutes: number }>;
  running: { started_at: string; suspended_at: string | null; status: 'running' | 'suspended' } | null;
}

/** P-10b (D-68) — מנהלת חשבונות: a record, not a user or an employee. */
export interface Accountant {
  id: string;
  name: string;
  email: string;
  address: string | null;
  note: string | null;
  created_at: string;
  updated_at: string;
}
